import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import { fetchAuthSession } from 'aws-amplify/auth';
import { UserContext } from '../App';
import { FaSpinner, FaTrash, FaEdit, FaUpload, FaPlus, FaSearch } from 'react-icons/fa';
import * as XLSX from 'xlsx';
import Logo from './Logo';

// --- Modal para Procesamiento de Excel ---
function ExcelProcessingModal({
  isOpen,
  onClose,
  step,
  sheets,
  selectedSheet,
  onSheetSelect,
  headers,
  data,
  columnMapping,
  onColumnMappingUpdate,
  requiredFields,
  isMappingComplete,
  onProceedToValidation,
  validationData,
  groupedValidationData,
  onUpdateValidationData,
  validationErrors,
  hasValidationErrors,
  hasFieldError,
  getFieldError,
  onFinalize,
  onFileUpload,
  onBackToMapping,
  message,
  isError,
  isUpdatingCollection,
  collectionToUpdate,
  handleNumericInputChange,
  formatCurrency,
  recordsToDelete,
  toggleRecordForDeletion,
  selectAllForDeletion,
  deselectAllForDeletion,
  recordsToUpdateCount,
  recordsToCreateCount,
  recordsToDeleteCount
}) {
  const [collectionName, setCollectionName] = useState('');
  const [uploadFile, setUploadFile] = useState(null);
  const [dragActive, setDragActive] = useState(false);

  useEffect(() => {
    if (!isOpen) {
      setCollectionName('');
      setUploadFile(null);
      setDragActive(false);
      // Reset group states
      setExpandedGroups(new Set(['updates', 'creates']));
      setGroupPages({
        updates: 1,
        creates: 1,
        deletes: 1,
        errors: 1
      });
    }
  }, [isOpen]);

  // --- Paginación para la vista de validación ---
  const VALIDATION_PAGE_SIZE = 40;
  const [validationCurrentPage, setValidationCurrentPage] = useState(1);
  
  // --- Estados para grupos expandibles ---
  const [expandedGroups, setExpandedGroups] = useState(new Set(['updates', 'creates', 'maintains'])); // Expandir por defecto
  const [groupPages, setGroupPages] = useState({
    updates: 1,
    creates: 1,
    maintains: 1,
    deletes: 1,
    errors: 1
  });
  
  // --- Estados para control de eliminación por usuario ---
  const [userMarkedForDeletion, setUserMarkedForDeletion] = useState(new Set());

  const getPaginatedValidationRecords = () => {
    if (!Array.isArray(validationData)) return [];
    // Para la tabla "preview" ignoramos los "isNotFound" (éstos se listan en su sección para eliminar)
    const onlyEditable = validationData.filter(r => !r.isNotFound);
    const start = (validationCurrentPage - 1) * VALIDATION_PAGE_SIZE;
    return onlyEditable.slice(start, start + VALIDATION_PAGE_SIZE);
  };

  const getValidationTotalPages = () => {
    if (!Array.isArray(validationData)) return 1;
    const count = validationData.filter(r => !r.isNotFound).length;
    return Math.max(1, Math.ceil(count / VALIDATION_PAGE_SIZE));
  };

  const handleValidationPageChange = (newPage) => {
    const total = getValidationTotalPages();
    if (newPage >= 1 && newPage <= total) {
      setValidationCurrentPage(newPage);
    }
  };

  // --- Funciones para grupos expandibles ---
  const toggleGroupExpansion = (groupKey) => {
    const newExpanded = new Set(expandedGroups);
    if (newExpanded.has(groupKey)) {
      newExpanded.delete(groupKey);
    } else {
      newExpanded.add(groupKey);
    }
    setExpandedGroups(newExpanded);
  };

  const getPaginatedGroupRecords = (records, groupKey) => {
    const page = groupPages[groupKey] || 1;
    const startIndex = (page - 1) * VALIDATION_PAGE_SIZE;
    const endIndex = startIndex + VALIDATION_PAGE_SIZE;
    return records.slice(startIndex, endIndex);
  };

  const getGroupTotalPages = (records) => {
    return Math.max(1, Math.ceil(records.length / VALIDATION_PAGE_SIZE));
  };

  const handleGroupPageChange = (groupKey, newPage, totalRecords) => {
    const totalPages = getGroupTotalPages(totalRecords);
    if (newPage >= 1 && newPage <= totalPages) {
      setGroupPages(prev => ({
        ...prev,
        [groupKey]: newPage
      }));
    }
  };

  // --- Funciones para control de eliminación por usuario ---
  const toggleUserDeletion = (amount) => {
    const newSet = new Set(userMarkedForDeletion);
    if (newSet.has(amount)) {
      newSet.delete(amount);
    } else {
      newSet.add(amount);
    }
    setUserMarkedForDeletion(newSet);
  };

  const toggleUserMarkForDeletion = toggleUserDeletion;

  const selectAllMaintainForDeletion = (records) => {
    const amounts = records.map(r => r.data.amount);
    const newSet = new Set([...userMarkedForDeletion, ...amounts]);
    setUserMarkedForDeletion(newSet);
  };

  const deselectAllMaintainForDeletion = (records) => {
    const amounts = records.map(r => r.data.amount);
    const newSet = new Set(userMarkedForDeletion);
    amounts.forEach(amount => newSet.delete(amount));
    setUserMarkedForDeletion(newSet);
  };

  // Función para renderizar vista completa de actualizaciones con grupos expandibles
  const renderValidationSection = () => {
    // Separar registros en categorías mejoradas
    const recordsToUpdate = validationData.filter(r => !r.isNew && r.hasChanges);
    const recordsToCreate = validationData.filter(r => r.isNew);
    const recordsToMaintain = validationData.filter(r => !r.isNew && !r.hasChanges && !r.isNotFound);
    const recordsNotFound = validationData.filter(r => r.isNotFound);
    
    // Filtrar recordsToMaintain que NO están marcados para eliminar por el usuario
    const recordsActuallyToMaintain = recordsToMaintain.filter(r => !userMarkedForDeletion.has(r.data.amount));
    
    // Clasificar registros finales para eliminación
    const allRecordsToDelete = [
      // Registros no encontrados que el usuario quiere eliminar (de recordsToDelete prop)
      ...recordsNotFound.filter(r => recordsToDelete.has(r.data.amount)),
      // Registros marcados por el usuario para eliminación (de userMarkedForDeletion state)
      ...recordsToMaintain.filter(r => userMarkedForDeletion.has(r.data.amount))
    ];
    
    const recordsWithErrors = validationData.filter(r => 
      validationErrors.some(error => error.rowIndex === r.rowIndex)
    );

    const renderGroupHeader = (groupKey, icon, title, count, description) => (
      <div 
        className="group-header"
        onClick={() => toggleGroupExpansion(groupKey)}
      >
        <div className="group-header-left">
          <span className={`group-expand-icon ${expandedGroups.has(groupKey) ? 'expanded' : ''}`}>
            ▶
          </span>
          <span className="group-icon">{icon}</span>
          <span className="group-title">{title} ({count})</span>
        </div>
        {description && (
          <span className="group-description">{description}</span>
        )}
      </div>
    );

    const renderGroupPagination = (groupKey, records) => {
      const totalPages = getGroupTotalPages(records);
      const currentPage = groupPages[groupKey] || 1;
      const paginatedRecords = getPaginatedGroupRecords(records, groupKey);
      const startRecord = ((currentPage - 1) * VALIDATION_PAGE_SIZE) + 1;
      const endRecord = startRecord + paginatedRecords.length - 1;

      if (totalPages <= 1) return null;

      return (
        <div className="group-pagination">
          <button
            type="button"
            className="btn btn-outline btn-sm"
            onClick={() => handleGroupPageChange(groupKey, currentPage - 1, records)}
            disabled={currentPage === 1}
          >
            ‹ Anterior
          </button>
          
          <div className="pagination-info">
            Página {currentPage} de {totalPages} • Registros {startRecord} al {endRecord} ({records.length} en total)
          </div>
          
          <button
            type="button"
            className="btn btn-outline btn-sm"
            onClick={() => handleGroupPageChange(groupKey, currentPage + 1, records)}
            disabled={currentPage === totalPages}
          >
            Siguiente ›
          </button>
        </div>
      );
    };
    
    return (
      <div className="validation-sections">
        {/* Registros con errores */}
        {recordsWithErrors.length > 0 && (
          <div className="validation-section error-section">
            {renderGroupHeader('errors', '⚠️', 'Registros con errores', recordsWithErrors.length, 'Corrige estos errores antes de continuar')}
            
            {expandedGroups.has('errors') && (
              <>
                <div className="records-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Fila</th>
                        <th>Valor</th>
                        <th>Código</th>
                        <th>Errores</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getPaginatedGroupRecords(recordsWithErrors, 'errors').map((record) => (
                        <tr key={record.rowIndex} className="error-record">
                          <td>#{record.rowIndex}</td>
                          <td>
                            <input
                              type="text"
                              value={record.data.amount ? Number(record.data.amount).toLocaleString('es-CO') : ''}
                              onChange={(e) => handleNumericInputChange(e, record.rowIndex, 'amount', onUpdateValidationData)}
                              className={`inline-edit-input amount-input ${hasFieldError(record.rowIndex, 'amount') ? 'error' : ''}`}
                              placeholder="0"
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              value={record.data.code || ''}
                              onChange={(e) => onUpdateValidationData(record.rowIndex, 'code', e.target.value)}
                              className={`inline-edit-input ${hasFieldError(record.rowIndex, 'code') ? 'error' : ''}`}
                              placeholder="CÓDIGO"
                            />
                          </td>
                          <td className="error-messages">
                            {validationErrors.filter(error => error.rowIndex === record.rowIndex).map((error, idx) => (
                              <div key={idx} className="error-list">
                                {Object.entries(error.errors).map(([field, message]) => (
                                  <div key={field} className="error-item">
                                    <strong>{field}:</strong> {message}
                                  </div>
                                ))}
                              </div>
                            ))}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {renderGroupPagination('errors', recordsWithErrors)}
              </>
            )}
          </div>
        )}

        {/* Registros a actualizar */}
        {recordsToUpdate.length > 0 && (
          <div className="validation-section update-section">
            {renderGroupHeader('updates', '📝', 'Registros a actualizar', recordsToUpdate.length, 'Bonos existentes que serán modificados')}
            
            {expandedGroups.has('updates') && (
              <>
                <div className="records-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Valor</th>
                        <th>Código</th>
                        <th>Cambios</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getPaginatedGroupRecords(recordsToUpdate, 'updates').map((record) => (
                        <tr key={record.rowIndex} className="update-record">
                          <td>
                            <input
                              type="text"
                              value={record.data.amount ? Number(record.data.amount).toLocaleString('es-CO') : ''}
                              onChange={(e) => handleNumericInputChange(e, record.rowIndex, 'amount', onUpdateValidationData)}
                              className={`inline-edit-input amount-input ${hasFieldError(record.rowIndex, 'amount') ? 'error' : ''}`}
                              placeholder="0"
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              value={record.data.code || ''}
                              onChange={(e) => onUpdateValidationData(record.rowIndex, 'code', e.target.value)}
                              className={`inline-edit-input ${hasFieldError(record.rowIndex, 'code') ? 'error' : ''}`}
                              placeholder="CÓDIGO"
                            />
                          </td>
                          <td className="changes-column">
                            {record.changes && Object.keys(record.changes).length > 0 ? (
                              <div className="changes-list">
                                {Object.entries(record.changes).map(([field, change]) => (
                                  <div key={field} className="change-item">
                                    <strong>{field}:</strong> {change.oldValue} → {change.newValue}
                                  </div>
                                ))}
                              </div>
                            ) : (
                              <span className="no-changes">Sin cambios detectados</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {renderGroupPagination('updates', recordsToUpdate)}
              </>
            )}
          </div>
        )}

        {/* Registros nuevos */}
        {recordsToCreate.length > 0 && (
          <div className="validation-section create-section">
            {renderGroupHeader('creates', '➕', 'Registros nuevos', recordsToCreate.length, 'Bonos que se crearán por primera vez')}
            
            {expandedGroups.has('creates') && (
              <>
                <div className="records-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Valor</th>
                        <th>Código</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getPaginatedGroupRecords(recordsToCreate, 'creates').map((record) => (
                        <tr key={record.rowIndex} className="new-record">
                          <td>
                            <input
                              type="text"
                              value={record.data.amount ? Number(record.data.amount).toLocaleString('es-CO') : ''}
                              onChange={(e) => handleNumericInputChange(e, record.rowIndex, 'amount', onUpdateValidationData)}
                              className={`inline-edit-input amount-input ${hasFieldError(record.rowIndex, 'amount') ? 'error' : ''}`}
                              placeholder="0"
                            />
                          </td>
                          <td>
                            <input
                              type="text"
                              value={record.data.code || ''}
                              onChange={(e) => onUpdateValidationData(record.rowIndex, 'code', e.target.value)}
                              className={`inline-edit-input ${hasFieldError(record.rowIndex, 'code') ? 'error' : ''}`}
                              placeholder="CÓDIGO"
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {renderGroupPagination('creates', recordsToCreate)}
              </>
            )}
          </div>
        )}

        {/* Registros a mantener */}
        {recordsActuallyToMaintain.length > 0 && (
          <div className="validation-section maintain-section">
            {renderGroupHeader('maintain', '✅', 'Registros a mantener', recordsActuallyToMaintain.length, 'Registros que permanecerán sin cambios')}
            
            {expandedGroups.has('maintain') && (
              <>
                <div className="records-table maintain-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Marcar para eliminación</th>
                        <th>Valor</th>
                        <th>Código</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getPaginatedGroupRecords(recordsActuallyToMaintain, 'maintain').map((record) => (
                        <tr key={record.data.amount} className="maintain-record">
                          <td>
                            <input
                              type="checkbox"
                              checked={userMarkedForDeletion.has(record.data.amount)}
                              onChange={() => toggleUserMarkForDeletion(record.data.amount)}
                            />
                          </td>
                          <td>${Number(record.data.amount).toLocaleString('es-CO')}</td>
                          <td>{record.data.code}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {renderGroupPagination('maintain', recordsActuallyToMaintain)}
              </>
            )}
          </div>
        )}

        {/* Registros a eliminar */}
        {allRecordsToDelete.length > 0 && (
          <div className="validation-section delete-section">
            {renderGroupHeader('delete', '🗑️', 'Registros a eliminar', allRecordsToDelete.length, 'Todos los registros marcados para eliminación')}
            
            {expandedGroups.has('delete') && (
              <>
                <div className="records-table delete-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Desmarcar</th>
                        <th>Valor</th>
                        <th>Código</th>
                        <th>Tipo</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getPaginatedGroupRecords(allRecordsToDelete, 'delete').map((record) => (
                        <tr key={record.data.amount} className="delete-record">
                          <td>
                            <input
                              type="checkbox"
                              checked={
                                record.isNotFound 
                                  ? recordsToDelete.has(record.data.amount)
                                  : userMarkedForDeletion.has(record.data.amount)
                              }
                              onChange={() => {
                                if (record.isNotFound) {
                                  toggleRecordForDeletion(record.data.amount);
                                } else {
                                  toggleUserMarkForDeletion(record.data.amount);
                                }
                              }}
                            />
                          </td>
                          <td>${Number(record.data.amount).toLocaleString('es-CO')}</td>
                          <td>{record.data.code}</td>
                          <td>
                            <span className={`record-type ${record.isNotFound ? 'not-found' : 'user-marked'}`}>
                              {record.isNotFound ? 'No encontrado' : 'Marcado por usuario'}
                            </span>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {renderGroupPagination('delete', allRecordsToDelete)}
              </>
            )}
          </div>
        )}

        {/* Registros no encontrados (para eliminar) */}
        {recordsNotFound.length > 0 && (
          <div className="validation-section delete-section">
            {renderGroupHeader('deletes', '🗑️', 'Registros para eliminar', recordsNotFound.length, 'Registros que no aparecen en tu archivo')}
            
            {expandedGroups.has('deletes') && (
              <>
                <div className="selection-controls">
                  <button 
                    type="button" 
                    className="btn btn-sm btn-outline"
                    onClick={selectAllForDeletion}
                  >
                    Seleccionar todo
                  </button>
                  <button 
                    type="button" 
                    className="btn btn-sm btn-outline"
                    onClick={deselectAllForDeletion}
                  >
                    Quitar selección
                  </button>
                </div>

                <div className="records-table delete-table">
                  <table>
                    <thead>
                      <tr>
                        <th>Eliminar</th>
                        <th>Valor</th>
                        <th>Código</th>
                      </tr>
                    </thead>
                    <tbody>
                      {getPaginatedGroupRecords(recordsNotFound, 'deletes').map((record) => (
                        <tr key={record.data.amount} className={`not-found-record ${recordsToDelete.has(record.data.amount) ? 'selected-for-deletion' : ''}`}>
                          <td>
                            <input
                              type="checkbox"
                              checked={recordsToDelete.has(record.data.amount)}
                              onChange={() => toggleRecordForDeletion(record.data.amount)}
                            />
                          </td>
                          <td>${Number(record.data.amount).toLocaleString('es-CO')}</td>
                          <td>{record.data.code}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {renderGroupPagination('deletes', recordsNotFound)}
              </>
            )}
          </div>
        )}
      </div>
    );
  };

  if (!isOpen) return null;

  const handleDrag = (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.type === 'dragenter' || e.type === 'dragover') {
      setDragActive(true);
    } else if (e.type === 'dragleave') {
      setDragActive(false);
    }
  };

  const handleDrop = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setDragActive(false);
    
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      const file = e.dataTransfer.files[0];
      setUploadFile(file);
      handleFileUpload(file);
    }
  };

  const handleFileUpload = (file) => {
    if (step === 'upload' && onFileUpload) {
      onFileUpload(file);
    }
  };

  const handleFileInputChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setUploadFile(file);
      handleFileUpload(file);
    }
  };

  const renderStepIndicator = () => {
    const steps = [
      { key: 'upload', label: 'Cargar Archivo', icon: '📁' },
      { key: 'sheet-selection', label: 'Seleccionar Hoja', icon: '📄' },
      { key: 'column-mapping', label: 'Mapear Columnas', icon: '🔗' },
      { key: 'data-validation', label: 'Validar Datos', icon: '✅' }
    ];

    const currentStepIndex = steps.findIndex(s => s.key === step);

    return (
      <div className="step-indicator">
        {steps.map((stepItem, index) => (
          <div key={stepItem.key} className={`step-item ${index <= currentStepIndex ? 'active' : ''} ${index === currentStepIndex ? 'current' : ''}`}>
            <div className="step-icon">{stepItem.icon}</div>
            <div className="step-label">{stepItem.label}</div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content excel-modal">
        <div className="modal-header">
          <h3 className="modal-title">
            {isUpdatingCollection 
              ? `Actualizar Colección: ${collectionToUpdate}`
              : 'Crear Nueva Colección de Bonos'
            }
          </h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>

        {renderStepIndicator()}

        {message && (
          <div className={`message ${isError ? 'message-error' : 'message-success'}`}>
            {message}
          </div>
        )}

        <div className="modal-body">
          {/* Paso 1: Cargar Archivo */}
          {step === 'upload' && (
            <div className="upload-section">
              <h4>Selecciona tu archivo Excel</h4>
              <div 
                className={`upload-area ${dragActive ? 'drag-active' : ''}`}
                onDragEnter={handleDrag}
                onDragLeave={handleDrag}
                onDragOver={handleDrag}
                onDrop={handleDrop}
              >
                <div className="upload-content">
                  <div className="upload-icon">🎁</div>
                  <p className="upload-text">
                    Arrastra tu archivo Excel aquí o <span className="upload-link">haz clic para seleccionar</span>
                  </p>
                  <p className="upload-hint">Formatos soportados: .xlsx, .xls</p>
                  <p className="upload-example">Debe contener columnas: Valor/Amount y Código/Code</p>
                  <input
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleFileInputChange}
                    className="upload-input"
                  />
                </div>
              </div>
            </div>
          )}

          {/* Paso 2: Selección de Hoja */}
          {step === 'sheet-selection' && (
            <div className="sheet-selection">
              <h4>Selecciona la hoja a procesar</h4>
              <p>Tu archivo tiene múltiples hojas. Selecciona la que contiene los códigos de bonos:</p>
              <div className="sheet-list">
                {sheets.map(sheet => (
                  <button
                    key={sheet}
                    className={`sheet-item ${selectedSheet === sheet ? 'selected' : ''}`}
                    onClick={() => onSheetSelect(sheet)}
                  >
                    📄 {sheet}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Paso 3: Mapeo de Columnas */}
          {step === 'column-mapping' && (
            <div className="column-mapping">
              <h4>Mapear Columnas</h4>
              <p>Conecta las columnas de tu Excel con los campos requeridos:</p>
              
              <div className="mapping-grid">
                {requiredFields.map(field => (
                  <div key={field.key} className="mapping-row">
                    <div className="field-info">
                      <div className="field-header">
                        <span className="field-icon">{field.icon}</span>
                        <span className="field-label">{field.label}</span>
                        <span className="field-required">*</span>
                      </div>
                      <p className="field-description">{field.description}</p>
                    </div>
                    <div className="field-mapping">
                      <select
                        value={columnMapping[field.key] !== undefined ? columnMapping[field.key] : ''}
                        onChange={(e) => onColumnMappingUpdate(field.key, e.target.value)}
                        className="mapping-select"
                        data-field={field.key}
                      >
                        <option value="">Seleccionar columna...</option>
                        {headers.map((header, index) => (
                          <option key={index} value={index}>
                            {header}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>
                ))}
              </div>

              <div className="mapping-actions">
                <button 
                  className="btn btn-primary"
                  onClick={onProceedToValidation}
                  disabled={!isMappingComplete()}
                >
                  {message && !isError ? '➡️ Continuar' : 'Continuar a Validación'}
                </button>
              </div>
            </div>
          )}

          {/* Paso 4: Validación de Datos */}
          {step === 'data-validation' && (
            <div className="data-validation">
              <h4>Validar y Finalizar</h4>
              <p>Revisa los datos antes de {isUpdatingCollection ? 'actualizar' : 'crear'} la colección:</p>
              
              <div className="validation-summary">
                <div className="summary-item">
                  <span className="summary-label">Total de registros:</span>
                  <span className="summary-value">{validationData.length}</span>
                </div>
                {isUpdatingCollection && (
                  <>
                    <div className="summary-item">
                      <span className="summary-label">Registros a actualizar:</span>
                      <span className="summary-value update-count">{recordsToUpdateCount}</span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Registros nuevos:</span>
                      <span className="summary-value create-count">{recordsToCreateCount}</span>
                    </div>
                    <div className="summary-item">
                      <span className="summary-label">Registros a eliminar:</span>
                      <span className="summary-value delete-count">{recordsToDeleteCount}</span>
                    </div>
                  </>
                )}
                <div className="summary-item">
                  <span className="summary-label">Errores:</span>
                  <span className={`summary-value ${hasValidationErrors ? 'error-count' : ''}`}>
                    {validationErrors.length}
                  </span>
                </div>
              </div>

              {hasValidationErrors && (
                <div className="validation-errors-alert">
                  <div className="alert alert-error">
                    <span className="alert-icon">⚠️</span>
                    <div className="alert-content">
                      <strong>¡Atención!</strong> Se encontraron {validationErrors.length} errores en los datos.
                      <br />
                      Los campos con errores están marcados en rojo. Corrige todos los errores antes de continuar.
                    </div>
                  </div>
                </div>
              )}

              {/* Mostrar secciones de validación */}
              {isUpdatingCollection ? renderValidationSection() : (
                <div className="grouped-data-preview">
                  <div className="validation-section create-section">
                    <h4>➕ Registros nuevos ({validationData.length})</h4>
                    <div className="records-table">
                      <table>
                        <thead>
                          <tr>
                            <th>Valor</th>
                            <th>Código</th>
                          </tr>
                        </thead>
                        <tbody>
                          {getPaginatedValidationRecords().map((record, index) => (
                            <tr key={record.rowIndex} className="new-record">
                              <td>
                                <input
                                  type="text"
                                  value={record.data.amount ? Number(record.data.amount).toLocaleString('es-CO') : ''}
                                  onChange={(e) => handleNumericInputChange(e, record.rowIndex, 'amount', onUpdateValidationData)}
                                  className={`inline-edit-input amount-input ${hasFieldError(record.rowIndex, 'amount') ? 'error' : ''}`}
                                  placeholder="0"
                                />
                              </td>
                              <td>
                                <input
                                  type="text"
                                  value={record.data.code || ''}
                                  onChange={(e) => onUpdateValidationData(record.rowIndex, 'code', e.target.value)}
                                  className={`inline-edit-input ${hasFieldError(record.rowIndex, 'code') ? 'error' : ''}`}
                                  placeholder="CÓDIGO"
                                />
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    
                    {/* Paginación para registros nuevos */}
                    {getValidationTotalPages() > 1 && (
                      <div className="validation-pagination">
                        <button
                          type="button"
                          className="btn btn-outline btn-sm"
                          onClick={() => handleValidationPageChange(validationCurrentPage - 1)}
                          disabled={validationCurrentPage === 1}
                        >
                          ‹ Anterior
                        </button>
                        
                        <div className="pagination-info">
                          {(() => {
                            const totalRecords = validationData.filter(r => !r.isNotFound).length;
                            const currentRecords = getPaginatedValidationRecords().length;
                            const pageSize = 40; // VALIDATION_PAGE_SIZE
                            const startRecord = ((validationCurrentPage - 1) * pageSize) + 1;
                            const endRecord = startRecord + currentRecords - 1;
                            
                            return `Página ${validationCurrentPage} de ${getValidationTotalPages()} • Registros ${startRecord} al ${endRecord} (${totalRecords} en total)`;
                          })()}
                        </div>
                        
                        <button
                          type="button"
                          className="btn btn-outline btn-sm"
                          onClick={() => handleValidationPageChange(validationCurrentPage + 1)}
                          disabled={validationCurrentPage === getValidationTotalPages()}
                        >
                          Siguiente ›
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {!isUpdatingCollection && (
                <div className="collection-name-input">
                  <label className="form-label">Nombre de la colección</label>
                  <input
                    type="text"
                    className="form-input"
                    value={collectionName}
                    onChange={(e) => setCollectionName(e.target.value)}
                    placeholder="Por ejemplo, Retailer - Celulares o Retailer - Marca - Celulares"
                  />
                </div>
              )}

              <div className="validation-actions">
                <button 
                  className="btn btn-outline"
                  onClick={onBackToMapping}
                >
                  Volver al Mapeo
                </button>
                <button 
                  className="btn btn-primary"
                  onClick={() => onFinalize(collectionName)}
                  disabled={(!collectionName.trim() && !isUpdatingCollection) || hasValidationErrors}
                  title={hasValidationErrors ? 'Corrige todos los errores antes de continuar' : ''}
                >
                  {message && !isError && message.includes('Procesando') ? (
                    <>
                      <div className="loading-spinner-small" style={{ marginRight: '0.5rem' }}></div>
                      Procesando...
                    </>
                  ) : (
                    isUpdatingCollection ? 'Actualizar Colección' : 'Crear Colección'
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// --- Modal de Confirmación ---
function ConfirmationModal({ isOpen, onClose, onConfirm, title, message, confirmText, cancelText, isLoading }) {
  if (!isOpen) return null;

  return (
    <div className="modal-overlay">
      <div className="modal-content animate-fade-in" style={{ maxWidth: '500px' }}>
        <div className="modal-header">
          <h3>{title}</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        <div className="modal-body">
          <p>{message}</p>
        </div>
        
        <div className="modal-footer">
          <button 
            type="button" 
            className="btn btn-outline" 
            onClick={onClose}
            disabled={isLoading}
          >
            {cancelText || 'Cancelar'}
          </button>
          <button 
            type="button" 
            className="btn btn-primary" 
            onClick={onConfirm}
            disabled={isLoading}
          >
            {isLoading ? 'Procesando...' : (confirmText || 'Confirmar')}
          </button>
        </div>
      </div>
    </div>
  );
}

// --- Modal para Agregar Nuevo Bono ---
function AddBonusModal({ isOpen, onClose, onSubmit, newBonusData, setNewBonusData, isLoading }) {
  if (!isOpen) return null;

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!newBonusData.amount || !newBonusData.code) return;
    onSubmit();
  };

  return (
    <div className="modal-overlay">
      <div className="modal-content animate-fade-in" style={{ maxWidth: '500px' }}>
        <div className="modal-header">
          <h3>🎁 Agregar Nuevo Bono</h3>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        <form onSubmit={handleSubmit}>
          <div className="modal-body">
            <div className="form-group">
              <label className="form-label">Valor del Bono</label>
              <input
                type="text"
                className="form-input"
                placeholder="Ej: 50000"
                value={newBonusData.amount}
                onChange={(e) => {
                  const cleanValue = e.target.value.replace(/[^\d]/g, '');
                  setNewBonusData({ ...newBonusData, amount: cleanValue });
                }}
                autoFocus
                required
              />
              <small className="form-hint">Solo números, sin puntos ni comas</small>
            </div>
            
            <div className="form-group">
              <label className="form-label">Código del Bono</label>
              <input
                type="text"
                className="form-input"
                placeholder="Ej: BONO50K"
                value={newBonusData.code}
                onChange={(e) => {
                  const cleanValue = e.target.value.toUpperCase().replace(/\s+/g, ' ').trim();
                  setNewBonusData({ ...newBonusData, code: cleanValue });
                }}
                required
              />
              <small className="form-hint">Se convertirá automáticamente a mayúsculas</small>
            </div>
          </div>
          
          <div className="modal-footer">
            <button 
              type="button"
              className="btn btn-outline" 
              onClick={onClose}
              disabled={isLoading}
            >
              Cancelar
            </button>
            <button 
              type="submit"
              className="btn btn-primary" 
              disabled={isLoading || !newBonusData.amount || !newBonusData.code}
            >
              {isLoading ? 'Agregando...' : '✓ Agregar Bono'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// --- Componente principal ---
export default function BonusCodes() {
  const { userData } = useContext(UserContext);
  const navigate = useNavigate();
  const tenantId = localStorage.getItem('tenant_id') || userData.tenant_id;

  // Campos requeridos para el mapeo de bonos
  const requiredBonusFields = [
    {
      key: 'amount',
      label: 'Valor',
      description: 'Valor monetario del bono',
      icon: '💰'
    },
    {
      key: 'code',
      label: 'Código',
      description: 'Código del bono',
      icon: '🎫'
    }
  ];

  // Función para obtener headers con autenticación Cognito
  const getAuthHeaders = async () => {
    try {
      const session = await fetchAuthSession();
      const idToken = session.tokens?.idToken?.toString();
      
      if (!idToken) {
        throw new Error('No se pudo obtener el ID token');
      }
      
      return {
        'Authorization': `Bearer ${idToken}`,
        'Content-Type': 'application/json'
      };
    } catch (error) {
      console.error('Error al obtener el token de autenticación:', error);
      throw error;
    }
  };

  // Estados principales
  const [collections, setCollections] = useState([]);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [loadingCollections, setLoadingCollections] = useState(false);
  const [bonuses, setBonuses] = useState([]);
  const [loadingBonuses, setLoadingBonuses] = useState(false);
  const [editingBonuses, setEditingBonuses] = useState(new Set());
  const [deletingBonuses, setDeletingBonuses] = useState(new Set());
  const [bonusChanges, setBonusChanges] = useState({});
  
  // Estados de paginación
  const [currentPage, setCurrentPage] = useState(1);
  const [bonusesPerPage] = useState(40);
  
  // Estados para mensajes y errores
  const [message, setMessage] = useState('');
  const [isError, setIsError] = useState(false);
  const [summary, setSummary] = useState(null);

  // Estados para procesamiento de Excel
  const [showExcelModal, setShowExcelModal] = useState(false);
  const [excelProcessingStep, setExcelProcessingStep] = useState('upload');
  const [excelWorkbook, setExcelWorkbook] = useState(null);
  const [excelSheets, setExcelSheets] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState('');
  const [excelHeaders, setExcelHeaders] = useState([]);
  const [excelData, setExcelData] = useState([]);
  const [columnMapping, setColumnMapping] = useState({});
  const [validationData, setValidationData] = useState([]);
  const [groupedValidationData, setGroupedValidationData] = useState({});
  const [validationErrors, setValidationErrors] = useState([]);
  const [hasValidationErrors, setHasValidationErrors] = useState(false);

  // Estados para actualización
  const [isUpdatingCollection, setIsUpdatingCollection] = useState(false);
  const [collectionToUpdate, setCollectionToUpdate] = useState('');
  const [existingBonuses, setExistingBonuses] = useState([]);
  const [recordsToDelete, setRecordsToDelete] = useState(new Set());

  // Estados para confirmación
  const [showConfirmationModal, setShowConfirmationModal] = useState(false);
  const [confirmationAction, setConfirmationAction] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);

  // Estados de búsqueda y filtros
  const [searchTerm, setSearchTerm] = useState('');
  
  // Estados para agregar nuevo bono
  const [showAddBonusModal, setShowAddBonusModal] = useState(false);
  const [newBonusData, setNewBonusData] = useState({ amount: '', code: '' });


  // Fetch inicial de colecciones
  useEffect(() => {
    fetchCollections();
  }, []);

  // Fetch de bonos cuando cambia la colección seleccionada
  useEffect(() => {
    if (selectedCollection) {
      fetchBonuses(selectedCollection.collection_id);
    } else {
      setBonuses([]);
      setBonusChanges({});
      setEditingBonuses(new Set());
      setDeletingBonuses(new Set());
    }
  }, [selectedCollection]);

  // Función para obtener todas las colecciones
  const fetchCollections = async () => {
    setLoadingCollections(true);
    try {
      const response = await axios.get(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/bonuses_codes',
        { 
          headers: await getAuthHeaders(),
          params: { mode: 'all_collections' }
        }
      );
      const collectionsData = response.data.collections;
      console.log('🎁 [BonusCodes] Collections data received:', collectionsData, 'Type:', typeof collectionsData);
      
      // Transformar el objeto en array si es necesario
      let collectionsArray = [];
      if (Array.isArray(collectionsData)) {
        collectionsArray = collectionsData;
      } else if (collectionsData && typeof collectionsData === 'object') {
        // Si es un objeto, convertir a array
        collectionsArray = Object.keys(collectionsData).map(collectionId => ({
          collection_id: collectionId,
          collection_name: collectionsData[collectionId].collection_name,
          updated_at: collectionsData[collectionId].updated_at
        }));
        console.log('🎁 [BonusCodes] Transformed to array:', collectionsArray);
      }
      
      // Ordenar las colecciones por nombre en orden ascendente
      collectionsArray.sort((a, b) => {
        return a.collection_name.localeCompare(b.collection_name, 'es', { sensitivity: 'base' });
      });
      
      setCollections(collectionsArray);
    } catch (error) {
      console.error('Error al obtener colecciones:', error);
      setMessage('Error al cargar las colecciones.');
      setIsError(true);
      setCollections([]); // Asegurar que siempre sea un array
    } finally {
      setLoadingCollections(false);
    }
  };

  // Función para obtener bonos de una colección específica
  const fetchBonuses = async (collectionId) => {
    setLoadingBonuses(true);
    try {
      const response = await axios.get(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/bonuses_codes',
        { 
          headers: await getAuthHeaders(),
          params: { 
            mode: 'one_collection',
            collection_id: collectionId
          }
        }
      );
      const bonusesData = response.data.items;
      setBonuses(Array.isArray(bonusesData) ? bonusesData : []);
      setCurrentPage(1); // Reset pagination
    } catch (error) {
      console.error('Error al obtener bonos:', error);
      setMessage('Error al cargar los bonos de la colección.');
      setIsError(true);
      setBonuses([]); // Asegurar que siempre sea un array
    } finally {
      setLoadingBonuses(false);
    }
  };

  // Función para crear nueva colección
  const createCollection = async (collectionName, items) => {
    try {
      const response = await axios.post(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/bonuses_codes',
        {
          collection_name: collectionName,
          items: items
        },
        { headers: await getAuthHeaders() }
      );

      if (response.status === 201) {
        setMessage(`Colección creada exitosamente: ${response.data.collection_name}`);
        setIsError(false);
        setSummary(response.data.summary);
        
        // Refrescar lista de colecciones
        await fetchCollections();
        
        // Seleccionar la nueva colección automáticamente
        const newCollection = {
          collection_id: response.data.collection_id,
          collection_name: response.data.collection_name
        };
        setSelectedCollection(newCollection);
        
        return response.data;
      }
    } catch (error) {
      console.error('Error al crear colección:', error);
      const errorMessage = error.response?.data?.message || error.message;
      setMessage(`Error al crear la colección: ${errorMessage}`);
      setIsError(true);
      throw error;
    }
  };

  // Función para actualizar colección existente
  const updateCollection = async (collectionId, collectionName, items) => {
    try {
      const updateData = {
        collection_id: collectionId,
        items: items
      };
      
      // Solo incluir collection_name si cambió
      if (collectionName && collectionName !== collectionToUpdate) {
        updateData.collection_name = collectionName;
      }

      const response = await axios.patch(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/bonuses_codes',
        updateData,
        { headers: await getAuthHeaders() }
      );

      if (response.status === 200) {
        setMessage('Colección actualizada exitosamente');
        setIsError(false);
        setSummary(response.data.summary);
        
        // Refrescar lista de colecciones
        await fetchCollections();
        
        // Refrescar bonos de la colección actual
        if (selectedCollection && selectedCollection.collection_id === collectionId) {
          await fetchBonuses(collectionId);
        }
        
        return response.data;
      }
    } catch (error) {
      console.error('Error al actualizar colección:', error);
      const errorMessage = error.response?.data?.message || error.message;
      setMessage(`Error al actualizar la colección: ${errorMessage}`);
      setIsError(true);
      throw error;
    }
  };

  // Función para actualizar bonos existentes (PATCH)
  const patchBonuses = async (collectionId, items) => {
    try {
      const response = await axios.patch(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/bonuses_codes',
        {
          collection_id: collectionId,
          items: items
        },
        { headers: await getAuthHeaders() }
      );

      if (response.status === 200) {
        setMessage('Registros actualizados exitosamente');
        setIsError(false);
        
        // Refrescar bonos
        await fetchBonuses(collectionId);
        
        return response.data;
      }
    } catch (error) {
      console.error('Error al actualizar registros:', error);
      const errorMessage = error.response?.data?.message || error.message;
      setMessage(`Error al actualizar registros: ${errorMessage}`);
      setIsError(true);
      throw error;
    }
  };

  // Función para eliminar registros de bonos
  const deleteBonuses = async (collectionId, amounts) => {
    try {
      const response = await axios.delete(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/bonuses_codes',
        {
          headers: await getAuthHeaders(),
          data: {
            collection_id: collectionId,
            items: Array.from(amounts).map(amount => ({ amount }))
          }
        }
      );

      if (response.status === 200) {
        setMessage('Registros eliminados exitosamente');
        setIsError(false);
        
        // Refrescar bonos
        await fetchBonuses(collectionId);
        
        return response.data;
      }
    } catch (error) {
      console.error('Error al eliminar registros:', error);
      const errorMessage = error.response?.data?.message || error.message;
      setMessage(`Error al eliminar registros: ${errorMessage}`);
      setIsError(true);
      throw error;
    }
  };

  // === FUNCIONES PARA PROCESAMIENTO DE EXCEL ===

  // Función para resetear el estado del procesamiento de Excel
  const resetExcelProcessing = () => {
    setExcelProcessingStep('upload');
    setExcelWorkbook(null);
    setExcelSheets([]);
    setSelectedSheet('');
    setExcelHeaders([]);
    setExcelData([]);
    setColumnMapping({});
    setValidationData([]);
    setGroupedValidationData({});
    setValidationErrors([]);
    setHasValidationErrors(false);
    setIsUpdatingCollection(false);
    setCollectionToUpdate('');
    setExistingBonuses([]);
    setRecordsToDelete(new Set());
  };

  // Función para procesar archivo Excel
  const processExcelFile = async (file) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      
      reader.onload = (e) => {
        try {
          const data = new Uint8Array(e.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          
          const sheetNames = workbook.SheetNames;
          setExcelWorkbook(workbook);
          setExcelSheets(sheetNames);
          
          if (sheetNames.length === 1) {
            // Si solo hay una hoja, procesarla automáticamente
            setSelectedSheet(sheetNames[0]);
            processSelectedSheet(workbook, sheetNames[0]);
            resolve();
          } else {
            // Si hay múltiples hojas, permitir al usuario seleccionar
            setExcelProcessingStep('sheet-selection');
            resolve();
          }
        } catch (error) {
          reject(new Error('Error al leer el archivo Excel. Asegúrate de que sea un archivo Excel válido.'));
        }
      };
      
      reader.onerror = () => {
        reject(new Error('Error al leer el archivo.'));
      };
      
      reader.readAsArrayBuffer(file);
    });
  };

  // Función para procesar la hoja seleccionada
  const processSelectedSheet = (workbook, sheetName) => {
    try {
      const worksheet = workbook.Sheets[sheetName];
      const jsonData = XLSX.utils.sheet_to_json(worksheet, { header: 1 });
      
      if (jsonData.length === 0) {
        throw new Error('La hoja seleccionada está vacía.');
      }
      
      // Obtener headers (primera fila)
      const headers = jsonData[0].filter(header => header && header.toString().trim() !== '');
      
      if (headers.length === 0) {
        throw new Error('No se encontraron columnas válidas en la primera fila.');
      }
      
      // Obtener datos (filas restantes)
      const dataRows = jsonData.slice(1).filter(row => 
        row.some(cell => cell && cell.toString().trim() !== '')
      );
      
      if (dataRows.length === 0) {
        throw new Error('No se encontraron datos en la hoja seleccionada.');
      }
      
      setExcelHeaders(headers);
      setExcelData(dataRows);
      
      // Mapeo automático de columnas
      const autoMapping = performAutoMapping(headers);
      setColumnMapping(autoMapping);
      setExcelProcessingStep('column-mapping');
      
    } catch (error) {
      setMessage(`Error al procesar la hoja: ${error.message}`);
      setIsError(true);
    }
  };

  // Función para mapeo automático de columnas
  const performAutoMapping = (headers) => {
    const mapping = {};
    
    const patterns = {
      amount: ['amount', 'valor', 'monto', 'cantidad', 'precio', 'pesos'],
      code: ['code', 'codigo', 'código', 'bono', 'cupon', 'cupón', 'ticket']
    };
    
    headers.forEach((header, index) => {
      const headerLower = header.toString().toLowerCase().trim();
      
      for (const [fieldKey, patternList] of Object.entries(patterns)) {
        if (patternList.some(pattern => headerLower.includes(pattern))) {
          if (!mapping[fieldKey]) {
            mapping[fieldKey] = index;
          }
        }
      }
    });
    
    return mapping;
  };

  // Función para manejar la selección de hoja
  const handleSheetSelection = (sheetName) => {
    setSelectedSheet(sheetName);
    processSelectedSheet(excelWorkbook, sheetName);
  };

  // Función para actualizar mapeo de columnas
  const updateColumnMapping = (fieldKey, columnIndex) => {
    const newMapping = { ...columnMapping };
    
    const numericIndex = columnIndex === '' ? null : parseInt(columnIndex);
    
    // Remover mapeo previo de esta columna si existe
    if (numericIndex !== null) {
      Object.keys(newMapping).forEach(key => {
        if (newMapping[key] === numericIndex) {
          delete newMapping[key];
        }
      });
    }
    
    // Agregar nuevo mapeo o eliminar si es vacío
    if (numericIndex !== null && !isNaN(numericIndex)) {
      newMapping[fieldKey] = numericIndex;
    } else {
      delete newMapping[fieldKey];
    }
    
    setColumnMapping(newMapping);
  };

  // Verificar si el mapeo está completo
  const isMappingComplete = () => {
    return requiredBonusFields.every(field => columnMapping[field.key] !== undefined);
  };

  // Función para manejar la carga de archivo Excel
  const handleExcelFileUpload = async (file) => {
    if (!file) return;
    
    // Validar tipo de archivo
    const validTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      'application/vnd.ms-excel',
    ];
    
    if (!validTypes.includes(file.type) && !file.name.match(/\.(xlsx|xls)$/i)) {
      setMessage('Por favor selecciona un archivo Excel válido (.xlsx o .xls)');
      setIsError(true);
      return;
    }
    
    setMessage('Procesando archivo Excel...');
    setIsError(false);
    
    try {
      await processExcelFile(file);
    } catch (error) {
      setMessage(`Error al procesar archivo: ${error.message}`);
      setIsError(true);
      resetExcelProcessing();
    }
  };

  // Función para proceder a la validación de datos
  const proceedToDataValidation = async () => {
    if (!isMappingComplete()) {
      setMessage('Por favor, mapea todas las columnas requeridas antes de continuar.');
      setIsError(true);
      return;
    }
    
    setMessage('Procesando datos...');
    setIsError(false);
    
    try {
      // Preparar datos para validación
      let validationRows = excelData.map((row, index) => {
        const mappedData = {};
        requiredBonusFields.forEach(field => {
          const columnIndex = columnMapping[field.key];
          if (columnIndex !== undefined) {
            const rawValue = row[columnIndex] || '';
            mappedData[field.key] = cleanAndFormatValue(rawValue, field.key);
          }
        });
        return {
          rowIndex: index + 2,
          data: mappedData,
          errors: {},
          isNew: true,
          hasChanges: false,
          changes: {}
        };
      });

      // Eliminar duplicados basados en amount y code
      const uniqueRowsMap = new Map();
      validationRows.forEach(row => {
        const key = `${row.data.amount}-${row.data.code}`;
        if (!uniqueRowsMap.has(key)) {
          uniqueRowsMap.set(key, row);
        }
      });
      
      // Convertir de vuelta a array y ordenar por monto (menor a mayor)
      validationRows = Array.from(uniqueRowsMap.values()).sort((a, b) => {
        const amountA = parseInt(a.data.amount) || 0;
        const amountB = parseInt(b.data.amount) || 0;
        return amountA - amountB;
      });

      // Recalcular rowIndex después del ordenamiento
      validationRows = validationRows.map((row, index) => ({
        ...row,
        rowIndex: index + 2
      }));

      // Si estamos actualizando, verificar duplicados y cambios
      if (isUpdatingCollection && selectedCollection) {
        const checkedRows = await checkForUpdatesAndDuplicates(validationRows);
        setValidationData(checkedRows);
      } else {
        setValidationData(validationRows);
      }
      
      // Validar datos inicialmente
      validateDataAndDetectErrors(validationData.length > 0 ? validationData : validationRows);
      
      setExcelProcessingStep('data-validation');
      setMessage('');
      
    } catch (error) {
      console.error('Error al procesar datos:', error);
      setMessage('Error al procesar los datos. Inténtalo de nuevo.');
      setIsError(true);
    }
  };

  // Función para verificar duplicados y cambios en actualizaciones
  const checkForUpdatesAndDuplicates = async (validationRows) => {
    try {
      // Obtener bonos existentes de la colección
      const response = await axios.get(
        'https://4svb468mc6.execute-api.us-east-2.amazonaws.com/prod/v1/bonuses_codes',
        { 
          headers: await getAuthHeaders(),
          params: { 
            mode: 'one_collection',
            collection_id: selectedCollection.collection_id
          }
        }
      );

      const existing = response.data.items || [];
      setExistingBonuses(existing);

      // Crear mapa de registros existentes por amount
      const existingMap = new Map();
      existing.forEach(bonus => {
        existingMap.set(bonus.amount.toString(), bonus);
      });

      // Clasificar registros
      const checkedRows = validationRows.map(row => {
        const amount = row.data.amount;
        const existingRecord = existingMap.get(amount);
        
        if (existingRecord) {
          // Es una actualización - verificar cambios
          const changes = {};
          let hasChanges = false;
          
          if (existingRecord.code !== row.data.code) {
            changes.code = {
              oldValue: existingRecord.code,
              newValue: row.data.code
            };
            hasChanges = true;
          }
          
          return {
            ...row,
            isNew: false,
            existingRecord,
            hasChanges,
            changes
          };
        } else {
          // Es un registro nuevo
          return {
            ...row,
            isNew: true,
            hasChanges: true
          };
        }
      });

      // Encontrar registros que no aparecen en el archivo (para posible eliminación)
      const fileAmounts = new Set(validationRows.map(row => row.data.amount));
      const recordsNotFound = existing.filter(bonus => !fileAmounts.has(bonus.amount.toString()));
      
      // Agregar registros no encontrados al estado
      const notFoundRecords = recordsNotFound.map(bonus => ({
        amount: bonus.amount,
        code: bonus.code,
        isNotFound: true
      }));
      
      setValidationData([...checkedRows, ...notFoundRecords.map(r => ({
        data: r,
        isNotFound: true
      }))]);

      return checkedRows;
    } catch (error) {
      console.error('Error verificando duplicados:', error);
      return validationRows.map(row => ({
        ...row,
        isNew: true,
        hasChanges: true
      }));
    }
  };

  // Función para validar datos y detectar errores
  const validateDataAndDetectErrors = (data) => {
    const errors = [];
    
    data.filter(row => !row.isNotFound).forEach(row => {
      const rowErrors = {};
      let hasRowError = false;
      
      // Validar campos requeridos
      requiredBonusFields.forEach(field => {
        const value = row.data[field.key];
        
        if (!value || value.toString().trim() === '') {
          rowErrors[field.key] = 'Campo requerido';
          hasRowError = true;
        } else if (field.key === 'amount') {
          // Validar que el valor sea numérico y positivo
          const numValue = parseFloat(value);
          if (isNaN(numValue) || numValue <= 0) {
            rowErrors[field.key] = 'Valor numérico inválido o menor igual a cero';
            hasRowError = true;
          }
        }
      });
      
      if (hasRowError) {
        errors.push({
          rowIndex: row.rowIndex,
          errors: rowErrors,
          data: row.data
        });
      }
    });
    
    setValidationErrors(errors);
    setHasValidationErrors(errors.length > 0);
    
    return errors;
  };

  // Función auxiliar para verificar si un campo tiene error
  const hasFieldError = (rowIndex, fieldKey) => {
    const rowError = validationErrors.find(error => error.rowIndex === rowIndex);
    return rowError && rowError.errors[fieldKey];
  };

  // Función auxiliar para obtener mensaje de error de un campo
  const getFieldError = (rowIndex, fieldKey) => {
    const rowError = validationErrors.find(error => error.rowIndex === rowIndex);
    return rowError ? rowError.errors[fieldKey] : null;
  };

  // Función para actualizar datos inline
  const updateValidationData = (rowIndex, fieldKey, newValue) => {
    const updatedData = validationData.map(row => {
      if (row.rowIndex === rowIndex) {
        const cleanedValue = cleanAndFormatValue(newValue, fieldKey);
        const updatedRow = {
          ...row,
          data: {
            ...row.data,
            [fieldKey]: cleanedValue
          }
        };
        
        // Si estamos actualizando, recalcular cambios
        if (!row.isNew && row.existingRecord) {
          const changes = {};
          let hasChanges = false;
          
          requiredBonusFields.forEach(field => {
            const oldValue = row.existingRecord[field.key];
            const newValue = field.key === fieldKey ? cleanedValue : row.data[field.key];
            
            if (oldValue !== newValue) {
              changes[field.key] = {
                oldValue: oldValue,
                newValue: newValue
              };
              hasChanges = true;
            }
          });
          
          updatedRow.changes = changes;
          updatedRow.hasChanges = hasChanges;
        }
        
        return updatedRow;
      }
      return row;
    });
    
    setValidationData(updatedData);
    
    // Validar datos en tiempo real
    validateDataAndDetectErrors(updatedData);
  };

  // Función para validar y limpiar datos
  const cleanAndFormatValue = (value, fieldKey) => {
    if (value === null || value === undefined) {
      return '';
    }
    
    let cleanValue = value.toString().trim();
    
    if (fieldKey === 'amount') {
      // Remover caracteres no numéricos excepto punto y coma
      cleanValue = cleanValue.replace(/[^\d.,]/g, '');
      // Convertir coma a punto para decimales
      cleanValue = cleanValue.replace(',', '.');
      // Validar que sea un número válido
      const numValue = parseFloat(cleanValue);
      if (isNaN(numValue) || numValue < 0) {
        return '';
      }
      return Math.floor(numValue).toString(); // Bonos deben ser números enteros
    }
    
    if (fieldKey === 'code') {
      // Convertir a mayúsculas y limpiar espacios extra
      return cleanValue.toUpperCase().replace(/\s+/g, ' ').trim();
    }
    
    return cleanValue;
  };

  // Función para manejar cambios en inputs numéricos
  const handleNumericInputChange = (e, rowIndex, fieldKey, onUpdateValidationData) => {
    const input = e.target;
    const value = input.value;
    
    // Remover todo excepto dígitos
    let cleanValue = value.replace(/[^\d]/g, '');
    
    // Actualizar el valor
    onUpdateValidationData(rowIndex, fieldKey, cleanValue);
  };

  // Función para finalizar y enviar datos al servidor
  const finalizeExcelUpload = async (collectionName) => {
    if (!isUpdatingCollection && !collectionName.trim()) {
      setMessage('Por favor, ingresa un nombre para la colección.');
      setIsError(true);
      return;
    }
    
    try {
      // Filtrar y preparar datos
      const recordsToProcess = validationData.filter(row => 
        !row.isNotFound && (row.isNew || row.hasChanges)
      );
      
      if (recordsToProcess.length === 0 && recordsToDelete.size === 0) {
        setMessage('No se detectaron cambios. No hay nada que procesar.');
        setIsError(false);
        return;
      }
      
      // Preparar items para envío
      const items = recordsToProcess.map(row => ({
        amount: parseInt(row.data.amount),
        code: row.data.code
      }));
      
      setMessage('Procesando...');
      
      if (isUpdatingCollection) {
        // Actualizar colección existente
        if (items.length > 0) {
          await updateCollection(selectedCollection.collection_id, collectionName, items);
        }
        
        // Eliminar registros seleccionados
        if (recordsToDelete.size > 0) {
          await deleteBonuses(selectedCollection.collection_id, recordsToDelete);
        }
      } else {
        // Crear nueva colección
        await createCollection(collectionName, items);
      }
      
      // Actualizar lista de colecciones
      await fetchCollections();
      
      // Cerrar modal y resetear estados
      setShowExcelModal(false);
      resetExcelProcessing();
      
    } catch (error) {
      console.error('Error al finalizar:', error);
      // El error ya se maneja en las funciones create/update
    }
  };

  // Funciones para manejar eliminaciones masivas
  const toggleRecordForDeletion = (amount) => {
    const newSet = new Set(recordsToDelete);
    if (newSet.has(amount)) {
      newSet.delete(amount);
    } else {
      newSet.add(amount);
    }
    setRecordsToDelete(newSet);
  };



  const selectAllForDeletion = () => {
    const notFoundRecords = validationData.filter(row => row.isNotFound);
    const allAmounts = new Set(notFoundRecords.map(row => row.data.amount));
    setRecordsToDelete(allAmounts);
  };

  const deselectAllForDeletion = () => {
    setRecordsToDelete(new Set());
  };

  // Funciones para la interfaz principal

  // Función para abrir modal de crear nueva colección
  const openCreateCollectionModal = () => {
    setMessage('');
    setSummary(null);
    resetExcelProcessing();
    setShowExcelModal(true);
  };

  // Función para abrir modal de actualizar colección existente
  const openUpdateCollectionModal = async (collection) => {
    setMessage('');
    setSummary(null);
    resetExcelProcessing();                     // 1) primero resetea
    setIsUpdatingCollection(true);              // 2) luego marca modo actualización
    setCollectionToUpdate(collection.collection_name);
    setShowExcelModal(true);
  };

  // Función para seleccionar una colección
  const handleSelectCollection = (collection) => {
    if (selectedCollection && selectedCollection.collection_id === collection.collection_id) {
      // Deseleccionar si se hace clic en la misma colección
      setSelectedCollection(null);
    } else {
      setSelectedCollection(collection);
      // Limpiar cambios pendientes
      setBonusChanges({});
      setEditingBonuses(new Set());
      setDeletingBonuses(new Set());
    }
  };

  // Función para comenzar a editar un bono
  const startEditingBonus = (index) => {
    const newEditing = new Set(editingBonuses);
    const newDeleting = new Set(deletingBonuses);
    
    // Si está marcado para eliminación, quitarlo
    if (newDeleting.has(index)) {
      newDeleting.delete(index);
      setDeletingBonuses(newDeleting);
    }
    
    newEditing.add(index);
    setEditingBonuses(newEditing);
  };

  // Función para actualizar un bono mientras se edita
  const updateBonusChange = (index, field, value) => {
    const bonusKey = `${currentPage}-${index}`;
    const currentBonus = getPaginatedBonuses()[index];
    
    setBonusChanges(prev => {
      const existingChange = prev[bonusKey] || {};
      
      return {
        ...prev,
        [bonusKey]: {
          originalAmount: currentBonus.amount,
          originalCode: currentBonus.code,
          amount: existingChange.amount !== undefined ? existingChange.amount : currentBonus.amount,
          code: existingChange.code !== undefined ? existingChange.code : currentBonus.code,
          [field]: field === 'amount' ? parseInt(value) || 0 : value,
          hasChanges: true
        }
      };
    });
  };

  // Función para marcar un bono para eliminación
  const toggleBonusForDeletion = (index) => {
    const newDeleting = new Set(deletingBonuses);
    const newEditing = new Set(editingBonuses);
    
    if (newDeleting.has(index)) {
      newDeleting.delete(index);
    } else {
      // Si está en modo edición, quitarlo
      if (newEditing.has(index)) {
        newEditing.delete(index);
        setEditingBonuses(newEditing);
      }
      newDeleting.add(index);
    }
    setDeletingBonuses(newDeleting);
  };

  // Función para confirmar actualización de bonos
  const confirmUpdateBonuses = () => {
    const changesCount = Object.keys(bonusChanges).filter(key => bonusChanges[key].hasChanges).length;
    const deletionsCount = deletingBonuses.size;
    
    if (changesCount === 0 && deletionsCount === 0) {
      setMessage('No hay cambios para guardar.');
      setIsError(false);
      return;
    }

    const message = `¿Estás seguro de que quieres ${changesCount > 0 ? `actualizar ${changesCount} registro(s)` : ''}${changesCount > 0 && deletionsCount > 0 ? ' y ' : ''}${deletionsCount > 0 ? `eliminar ${deletionsCount} registro(s)` : ''}?`;
    
    setConfirmationAction({
      title: 'Confirmar Cambios',
      message,
      onConfirm: executeUpdateBonuses
    });
    setShowConfirmationModal(true);
  };

  // Función para manejar agregar nuevo bono
  const handleAddNewBonus = async () => {
    if (!selectedCollection || !newBonusData.amount || !newBonusData.code) return;
    
    setIsProcessing(true);
    try {
      const items = [{
        amount: parseInt(newBonusData.amount),
        code: newBonusData.code
      }];
      
      await updateCollection(selectedCollection.collection_id, null, items);
      
      // Limpiar formulario y cerrar modal
      setNewBonusData({ amount: '', code: '' });
      setShowAddBonusModal(false);
      
      setMessage('Bono agregado exitosamente');
      setIsError(false);
      
    } catch (error) {
      console.error('Error al agregar bono:', error);
      setMessage('Error al agregar el bono');
      setIsError(true);
    } finally {
      setIsProcessing(false);
    }
  };

  // Función para ejecutar la actualización de bonos
  const executeUpdateBonuses = async () => {
    if (!selectedCollection) return;
    
    setIsProcessing(true);
    try {
      // Separar cambios en 3 categorías:
      // 1. Ediciones (PATCH) - solo cambió el código, el amount permanece igual
      // 2. Cambios de valor (DELETE+POST) - cambió el amount
      // 3. Eliminaciones (DELETE) - marcados para borrar
      
      const itemsToUpdate = []; // Para PATCH
      const itemsToDeleteAndRecreate = []; // Para DELETE+POST
      const bonusesToDelete = new Set(); // Para DELETE puro
      
      // Procesar cambios de edición
      Object.entries(bonusChanges).forEach(([key, change]) => {
        if (change.hasChanges) {
          // Si el valor (amount) cambió, necesitamos DELETE+POST
          if (change.amount !== change.originalAmount) {
            bonusesToDelete.add(change.originalAmount);
            itemsToDeleteAndRecreate.push({
              amount: change.amount,
              code: change.code || change.originalCode
            });
          } else {
            // Solo cambió el código, usar PATCH
            itemsToUpdate.push({
              amount: change.originalAmount, // El amount original para identificar el registro
              code: change.code || change.originalCode
            });
          }
        }
      });

      // Agregar bonos marcados para eliminación (DELETE puro)
      deletingBonuses.forEach(index => {
        const bonus = getPaginatedBonuses()[index];
        if (bonus) {
          bonusesToDelete.add(bonus.amount);
        }
      });

      // Ejecutar operaciones en orden:
      // 1. PATCH para ediciones simples (solo código)
      if (itemsToUpdate.length > 0) {
        await patchBonuses(selectedCollection.collection_id, itemsToUpdate);
      }

      // 2. DELETE para registros que cambiaron de valor + eliminaciones
      if (bonusesToDelete.size > 0) {
        await deleteBonuses(selectedCollection.collection_id, bonusesToDelete);
      }

      // 3. POST para recrear registros con nuevos valores
      if (itemsToDeleteAndRecreate.length > 0) {
        await updateCollection(selectedCollection.collection_id, null, itemsToDeleteAndRecreate);
      }

      // Limpiar estados de edición
      setBonusChanges({});
      setEditingBonuses(new Set());
      setDeletingBonuses(new Set());
      
      setShowConfirmationModal(false);
      
    } catch (error) {
      // Error ya manejado en las funciones update/delete/patch
    } finally {
      setIsProcessing(false);
    }
  };

  // Función para cancelar cambios
  const cancelChanges = () => {
    setBonusChanges({});
    setEditingBonuses(new Set());
    setDeletingBonuses(new Set());
    setMessage('Cambios cancelados.');
    setIsError(false);
  };

  // Función para obtener bonos paginados
  const getPaginatedBonuses = () => {
    if (!Array.isArray(bonuses)) return [];
    let filteredBonuses = bonuses;
    
    // Aplicar filtro de búsqueda
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filteredBonuses = bonuses.filter(bonus => 
        bonus.code.toLowerCase().includes(search) ||
        bonus.amount.toString().includes(search)
      );
    }
    
    const startIndex = (currentPage - 1) * bonusesPerPage;
    const endIndex = startIndex + bonusesPerPage;
    return filteredBonuses.slice(startIndex, endIndex);
  };

  // Función para obtener el total de páginas
  const getTotalPages = () => {
    if (!Array.isArray(bonuses)) return 1;
    let filteredBonuses = bonuses;
    
    if (searchTerm.trim()) {
      const search = searchTerm.toLowerCase();
      filteredBonuses = bonuses.filter(bonus => 
        bonus.code.toLowerCase().includes(search) ||
        bonus.amount.toString().includes(search)
      );
    }
    
    return Math.ceil(filteredBonuses.length / bonusesPerPage);
  };

  // Función para cambiar página
  const handlePageChange = (newPage) => {
    const totalPages = getTotalPages();
    if (newPage >= 1 && newPage <= totalPages) {
      setCurrentPage(newPage);
    }
  };

  // Función para formatear moneda
  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-CO', {
      style: 'currency',
      currency: 'COP',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0
    }).format(amount);
  };

  // Calcular contadores para resumen
  const recordsToUpdateCount = validationData.filter(r => !r.isNew && r.hasChanges).length;
  const recordsToCreateCount = validationData.filter(r => r.isNew).length;
  const recordsToDeleteCount = recordsToDelete.size;

  return (
    <div className="precios-container">
      <header className="header">
        <Logo 
          variant="horizontal" 
          size="medium" 
          onClick={() => navigate('/dashboard')} 
        />
        <div className="header-title">Códigos de Bonos</div>
        <div className="user-menu">
          <span>Hola, {userData.user_name || 'Usuario'}</span>
          <button className="btn btn-outline" onClick={() => navigate('/dashboard')}>Volver al Dashboard</button>
        </div>
      </header>

      
      {/* Layout Principal con Grid */} 
      <div className="precios-layout container">
        {/* Columna Izquierda: Lista de Colecciones */}
        <div className="catalog-list-column card">
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
            <h3 className="column-title">Colecciones de Bonos</h3>
            <button className="btn btn-primary btn-sm" onClick={openCreateCollectionModal}>
              + Nueva
            </button>
          </div>

          {loadingCollections ? <div className="loading-spinner-small"></div> : (!collections || collections.length === 0) ? (
              <p className="empty-state">No hay colecciones.</p>
          ) : (
              Array.isArray(collections) && collections.map(collection => (
                <div
                  key={collection.collection_id}
                  className={`collection-item ${selectedCollection && selectedCollection.collection_id === collection.collection_id ? 'selected' : ''}`}
                  onClick={() => handleSelectCollection(collection)}
                >
                  <div className="collection-info">
                    <span className="collection-icon">🎁</span>
                    <div className="collection-details">
                      <span className="collection-name">{collection.collection_name}</span>
                      <span className="collection-id">ID: {collection.collection_id}</span>
                    </div>
                  </div>
                  {selectedCollection && selectedCollection.collection_id === collection.collection_id && (
                    <button
                      className="btn btn-sm btn-outline update-btn"
                      onClick={(e) => {
                        e.stopPropagation();
                        openUpdateCollectionModal(collection);
                      }}
                      title="Actualizar colección masivamente"
                    >
                      <FaUpload />
                    </button>
                  )}
                </div>
              ))
          )}
        </div>

        {/* Columna Derecha: Detalles y Tabla de Bonos */}
        <div className="pricing-column card">
          {!selectedCollection ? (
            <div className="empty-state">
              <div className="empty-content">
                <div className="empty-icon">🎁</div>
                <h3>Selecciona una colección</h3>
                <p>Elige una colección de bonos del panel izquierdo para ver y editar sus códigos</p>
              </div>
            </div>
          ) : (
            <div className="pricing-section">
              <div className="table-header">
                <h4 className="table-title">{selectedCollection.collection_name}</h4>
              </div>

              <div className="pricing-controls">
                <div className="search-box">
                  <FaSearch className="search-icon" />
                  <input
                    type="text"
                    placeholder="Buscar por código o valor..."
                    value={searchTerm}
                    onChange={(e) => {
                      setSearchTerm(e.target.value);
                      setCurrentPage(1);
                    }}
                  />
                </div>
                
                <div className="controls-right">
                  <button
                    className="btn btn-success btn-sm add-bonus-btn"
                    onClick={() => setShowAddBonusModal(true)}
                    title="Agregar nuevo bono"
                  >
                    <FaPlus /> Nuevo Bono
                  </button>
                </div>
              </div>
              
              {/* Contador total de bonos */}
              {bonuses.length > 0 && (
                <div className="table-summary">
                  <span className="total-count">Total: {bonuses.length} {bonuses.length === 1 ? 'bono' : 'bonos'}</span>
                </div>
              )}
              
              {/* Controles de edición centralizados */}
              {(Object.keys(bonusChanges).some(key => bonusChanges[key].hasChanges) || deletingBonuses.size > 0) && (
                <div className="edit-controls-centered">
                  <button
                    className="btn btn-outline btn-sm cancel-btn"
                    onClick={cancelChanges}
                    title="Cancelar cambios"
                  >
                    ✖ Cancelar
                  </button>
                  <button
                    className="btn btn-primary btn-sm update-btn"
                    onClick={confirmUpdateBonuses}
                    title="Guardar cambios"
                  >
                    ✓ Actualizar
                  </button>
                </div>
              )}

              {loadingBonuses ? (
                <div className="loading-container">
                  <div className="loading-spinner"></div>
                  <p>Cargando bonos...</p>
                </div>
              ) : bonuses.length === 0 ? (
                <div className="empty-state">
                  <p>Esta colección no tiene bonos</p>
                  <button
                    className="btn btn-primary"
                    onClick={() => openUpdateCollectionModal(selectedCollection)}
                  >
                    <FaUpload /> Subir Bonos
                  </button>
                </div>
              ) : (
                  <>
                    <div className="table-wrapper">
                      <table>
                        <thead>
                          <tr>
                            <th>Acciones</th>
                            <th>Valor</th>
                            <th>Código</th>
                          </tr>
                        </thead>
                      <tbody>
                        {getPaginatedBonuses().map((bonus, index) => {
                          const bonusKey = `${currentPage}-${index}`;
                          const change = bonusChanges[bonusKey];
                          const isEditing = editingBonuses.has(index);
                          const isDeleting = deletingBonuses.has(index);
                          
                          return (
                            <tr 
                              key={`${bonus.amount}-${bonus.code}`}
                              className={`
                                ${change && change.hasChanges ? 'row-edited' : ''}
                                ${isDeleting ? 'row-deleting' : ''}
                              `}
                            >
                              <td className="actions-cell">
                                <div className="action-buttons">
                                  {!isEditing ? (
                                    <button
                                      className="btn btn-sm btn-outline"
                                      onClick={() => startEditingBonus(index)}
                                      title="Editar"
                                    >
                                      <FaEdit />
                                    </button>
                                  ) : (
                                    <button
                                      className="btn btn-sm btn-success"
                                      onClick={() => {
                                        const newEditing = new Set(editingBonuses);
                                        newEditing.delete(index);
                                        setEditingBonuses(newEditing);
                                      }}
                                      title="Confirmar cambios"
                                    >
                                      ✓
                                    </button>
                                  )}
                                  <button
                                    className={`btn btn-sm ${isDeleting ? 'btn-danger' : 'btn-outline'}`}
                                    onClick={() => toggleBonusForDeletion(index)}
                                    title={isDeleting ? 'No eliminar' : 'Marcar para eliminación'}
                                  >
                                    <FaTrash />
                                  </button>
                                </div>
                              </td>
                              <td>
                                {isEditing ? (
                                  <input
                                    type="text"
                                    className="inline-edit-input amount-input"
                                    value={change ? (change.amount || bonus.amount).toLocaleString('es-CO') : bonus.amount.toLocaleString('es-CO')}
                                    onChange={(e) => {
                                      const cleanValue = e.target.value.replace(/[^\d]/g, '');
                                      updateBonusChange(index, 'amount', cleanValue);
                                    }}
                                    placeholder="Valor"
                                  />
                                ) : (
                                  <span className={change && change.amount !== bonus.amount ? 'value-changed' : ''}>
                                    {formatCurrency(change && change.amount ? change.amount : bonus.amount)}
                                  </span>
                                )}
                              </td>
                              <td>
                                {isEditing ? (
                                  <input
                                    type="text"
                                    className="inline-edit-input"
                                    value={change ? (change.code || bonus.code) : bonus.code}
                                    onChange={(e) => updateBonusChange(index, 'code', e.target.value)}
                                    placeholder="Código"
                                  />
                                ) : (
                                  <span className={change && change.code !== bonus.code ? 'value-changed' : ''}>
                                    {change && change.code ? change.code : bonus.code}
                                  </span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                        </tbody>
                      </table>
                    </div>

                    {/* Paginación */}
                  {getTotalPages() > 1 && (
                    <div className="pagination">
                      <button
                        className="btn btn-outline btn-sm"
                        onClick={() => handlePageChange(currentPage - 1)}
                        disabled={currentPage === 1}
                      >
                        ‹ Anterior
                      </button>
                      
                      <div className="pagination-info">
                        {(() => {
                          const totalBonuses = searchTerm ? 
                            bonuses.filter(bonus => 
                              bonus.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
                              bonus.amount.toString().includes(searchTerm)
                            ).length : 
                            bonuses.length;
                          const currentBonuses = getPaginatedBonuses().length;
                          const startRecord = ((currentPage - 1) * bonusesPerPage) + 1;
                          const endRecord = startRecord + currentBonuses - 1;
                          
                          return `Página ${currentPage} de ${getTotalPages()} • Registros ${startRecord} al ${endRecord} (${totalBonuses} en total)`;
                        })()}
                      </div>
                      
                      <button
                        className="btn btn-outline btn-sm"
                        onClick={() => handlePageChange(currentPage + 1)}
                        disabled={currentPage === getTotalPages()}
                      >
                        Siguiente ›
                      </button>
                    </div>
                  )}
                    </>
                )}
            </div>
          )}

          {/* Mensajes y resumen */}
          {message && (
            <div className={`message ${isError ? 'message-error' : 'message-success'}`}>
              {message}
            </div>
          )}

          {summary && (
            <div className="results-summary">
              <div className="summary-header">
                <h4 className="summary-title">📈 Resumen de Operación</h4>
                <button 
                  className="summary-close-btn"
                  onClick={() => setSummary(null)}
                  title="Cerrar resumen"
                >
                  ×
                </button>
              </div>
              <div className="summary-stats">
                {summary.created > 0 && (
                  <div className="stat-card success">
                    <div className="stat-number">{summary.created}</div>
                    <div className="stat-label">Bonos Creados</div>
                  </div>
                )}
                {summary.updated > 0 && (
                  <div className="stat-card info">
                    <div className="stat-number">{summary.updated}</div>
                    <div className="stat-label">Bonos Actualizados</div>
                  </div>
                )}
                {summary.conflicts > 0 && (
                  <div className="stat-card warning">
                    <div className="stat-number">{summary.conflicts}</div>
                    <div className="stat-label">Conflictos Resueltos</div>
                  </div>
                )}
                {summary.errors > 0 && (
                  <div className="stat-card error">
                    <div className="stat-number">{summary.errors}</div>
                    <div className="stat-label">Errores Encontrados</div>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Modal de procesamiento de Excel */}
      <ExcelProcessingModal
        isOpen={showExcelModal}
        onClose={() => {
          setShowExcelModal(false);
          resetExcelProcessing();
        }}
        step={excelProcessingStep}
        sheets={excelSheets}
        selectedSheet={selectedSheet}
        onSheetSelect={handleSheetSelection}
        headers={excelHeaders}
        data={excelData}
        columnMapping={columnMapping}
        onColumnMappingUpdate={updateColumnMapping}
        requiredFields={requiredBonusFields}
        isMappingComplete={isMappingComplete}
        onProceedToValidation={proceedToDataValidation}
        validationData={validationData}
        groupedValidationData={groupedValidationData}
        onUpdateValidationData={updateValidationData}
        validationErrors={validationErrors}
        hasValidationErrors={hasValidationErrors}
        hasFieldError={hasFieldError}
        getFieldError={getFieldError}
        onFinalize={finalizeExcelUpload}
        onFileUpload={handleExcelFileUpload}
        onBackToMapping={() => setExcelProcessingStep('column-mapping')}
        message={message}
        isError={isError}
        isUpdatingCollection={isUpdatingCollection}
        collectionToUpdate={collectionToUpdate}
        handleNumericInputChange={handleNumericInputChange}
        formatCurrency={formatCurrency}
        recordsToDelete={recordsToDelete}
        toggleRecordForDeletion={toggleRecordForDeletion}
        selectAllForDeletion={selectAllForDeletion}
        deselectAllForDeletion={deselectAllForDeletion}
        recordsToUpdateCount={recordsToUpdateCount}
        recordsToCreateCount={recordsToCreateCount}
        recordsToDeleteCount={recordsToDeleteCount}
      />

      {/* Modal para agregar nuevo bono */}
      <AddBonusModal
        isOpen={showAddBonusModal}
        onClose={() => setShowAddBonusModal(false)}
        onSubmit={handleAddNewBonus}
        newBonusData={newBonusData}
        setNewBonusData={setNewBonusData}
        isLoading={isProcessing}
      />

      {/* Modal de confirmación */}
      <ConfirmationModal
        isOpen={showConfirmationModal}
        onClose={() => setShowConfirmationModal(false)}
        onConfirm={confirmationAction?.onConfirm}
        title={confirmationAction?.title}
        message={confirmationAction?.message}
        confirmText="Sí, continuar"
        cancelText="Cancelar"
        isLoading={isProcessing}
      />
      
      {/* Estilos JSX */} 
      <style jsx>{`
          /* Estilos generales */
          .precios-container {
              min-height: 100vh;
              background-color: #f9fafb;
              font-family: 'Poppins', sans-serif;
          }
          
          /* Header */
          .header {
              background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
              padding: 1rem 2rem;
              display: flex;
              justify-content: space-between;
              align-items: center;
              box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
              position: sticky;
              top: 0;
              z-index: 50;
          }
          
          .header-title {
              color: white;
              font-size: 1.5rem;
              font-weight: 600;
              margin: 0;
          }
          
          .user-menu {
              display: flex;
              align-items: center;
              gap: 1rem;
              color: white;
          }
          
          .user-menu span {
              font-size: 0.9rem;
              opacity: 0.9;
          }
          
          /* Botones */
          .btn {
              display: inline-flex;
              align-items: center;
              justify-content: center;
              gap: 0.5rem;
              padding: 0.5rem 1rem;
              border: 1px solid transparent;
              border-radius: 6px;
              font-size: 0.875rem;
              font-weight: 500;
              text-decoration: none;
              cursor: pointer;
              transition: all 0.2s ease;
              white-space: nowrap;
          }
          
          .btn-primary {
              background-color: #6366f1;
              color: white;
              border-color: #6366f1;
          }
          
          .btn-primary:hover {
              background-color: #4f46e5;
              border-color: #4f46e5;
          }
          
          .btn-outline {
              background-color: transparent;
              color: white;
              border-color: rgba(255, 255, 255, 0.5);
          }
          
          .btn-outline:hover {
              background-color: rgba(255, 255, 255, 0.1);
              border-color: white;
          }
          
          .btn-sm {
              padding: 0.375rem 0.75rem;
              font-size: 0.8rem;
          }
          
          .btn-success {
              background-color: #10b981;
              color: white;
              border-color: #10b981;
          }
          
          .btn-danger {
              background-color: #ef4444;
              color: white;
              border-color: #ef4444;
          }
          
          /* Layout Principal */
          .precios-layout {
              display: grid;
              grid-template-columns: 320px 1fr;
              gap: 2rem;
              align-items: start;
              max-width: 100%;
              overflow-x: hidden;
              padding: 2rem;
          }
          
          .container {
              max-width: 1200px;
              margin: 0 auto;
              width: 100%;
          }
          
          .card {
              background-color: #fff;
              border-radius: 8px;
              padding: 1.5rem;
              box-shadow: 0 2px 4px rgba(0,0,0,0.05);
              overflow: hidden;
          }
          
          /* Columna Izquierda (Colecciones) */
          .catalog-list-column {
              height: calc(100vh - 140px);
              overflow-y: auto;
              min-width: 300px;
              flex-shrink: 0;
          }
          
          .column-title {
              margin: 0;
              color: #333;
              font-size: 1.25rem;
              font-weight: 600;
          }
          
          .collection-item {
              display: flex;
              align-items: center;
              justify-content: space-between;
              padding: 1rem;
              margin-bottom: 0.5rem;
              border-radius: 6px;
              cursor: pointer;
              transition: all 0.2s ease;
              border: 2px solid transparent;
          }
          
          .collection-item:hover {
              background-color: #f8fafc;
          }
          
          .collection-item.selected {
              background-color: #eff6ff;
              border-color: #6366f1;
          }
          
          .collection-info {
              display: flex;
              align-items: center;
              gap: 0.75rem;
              flex: 1;
          }
          
          .collection-icon {
              font-size: 1.2rem;
          }
          
          .collection-details {
              display: flex;
              flex-direction: column;
          }
          
          .collection-name {
              font-weight: 500;
              color: #1f2937;
              margin-bottom: 0.25rem;
          }
          
          .collection-id {
              font-size: 0.75rem;
              color: #6b7280;
          }
          
          /* Columna Derecha (Bonos) */
          .pricing-column {
              min-height: calc(100vh - 140px);
          }
          
          .pricing-section {
              height: 100%;
              display: flex;
              flex-direction: column;
          }
          
          .table-header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 1.5rem;
              padding-bottom: 1rem;
              border-bottom: 1px solid #e5e7eb;
          }
          
          .table-title {
              margin: 0;
              color: #1f2937;
              font-size: 1.25rem;
              font-weight: 600;
          }
          
          .table-info {
              display: flex;
              align-items: center;
              gap: 1rem;
              color: #6b7280;
              font-size: 0.875rem;
          }
          
          /* Controles de búsqueda */
          .pricing-controls {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 1.5rem;
              gap: 1rem;
          }
          
          .search-box {
              position: relative;
              flex: 1;
              max-width: 300px;
          }
          
          .search-box input {
              width: 100%;
              padding: 0.5rem 0.75rem 0.5rem 2.25rem;
              border: 1px solid #d1d5db;
              border-radius: 6px;
              font-size: 0.875rem;
              transition: border-color 0.2s ease;
          }
          
          .search-box input:focus {
              outline: none;
              border-color: #6366f1;
              box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
          }
          
          .search-icon {
              position: absolute;
              left: 0.75rem;
              top: 50%;
              transform: translateY(-50%);
              color: #6b7280;
              font-size: 0.875rem;
          }
          
          /* Tabla */
          .table-container {
              flex: 1;
              overflow: hidden;
              display: flex;
              flex-direction: column;
          }
          
          .table-wrapper {
              flex: 1;
              overflow-x: auto;
              border: 1px solid #e5e7eb;
              border-radius: 8px;
          }
          
          .table-wrapper table {
              width: 100%;
              border-collapse: collapse;
              font-size: 0.875rem;
          }
          
          .table-wrapper th {
              background-color: #f9fafb;
              padding: 0.75rem 1rem;
              text-align: left;
              font-weight: 600;
              color: #374151;
              border-bottom: 1px solid #e5e7eb;
              position: sticky;
              top: 0;
              z-index: 10;
          }
          
          .table-wrapper td {
              padding: 0.75rem 1rem;
              border-bottom: 1px solid #f3f4f6;
              vertical-align: middle;
          }
          
          .table-wrapper tr:hover {
              background-color: #f8fafc;
          }
          
          .row-edited {
              background-color: #fef3cd !important;
          }
          
          .row-deleting {
              background-color: #fee2e2 !important;
              opacity: 0.7;
          }
          
          .value-changed {
              color: #d97706;
              font-weight: 500;
          }
          
          /* Controles de acción */
          .actions-cell {
              width: 120px;
          }
          
          .action-buttons {
              display: flex;
              gap: 0.5rem;
          }
          
          .action-buttons .btn-outline {
              color: #6b7280;
              border-color: #d1d5db;
              background-color: white;
          }
          
          .action-buttons .btn-outline:hover {
              color: #374151;
              border-color: #9ca3af;
              background-color: #f9fafb;
          }
          
          .action-buttons .btn-success {
              color: white;
              background-color: #10b981;
              border-color: #10b981;
          }
          
          .action-buttons .btn-success:hover {
              background-color: #059669;
              border-color: #059669;
          }
          
          .action-buttons .btn-danger {
              color: white;
              background-color: #ef4444;
              border-color: #ef4444;
          }
          
          .action-buttons .btn-danger:hover {
              background-color: #dc2626;
              border-color: #dc2626;
          }
          
          .edit-controls {
              display: flex;
              gap: 0.5rem;
          }
          
          /* Inputs inline */
          .inline-edit-input {
              width: 100%;
              padding: 0.25rem 0.5rem;
              border: 1px solid #d1d5db;
              border-radius: 4px;
              font-size: 0.875rem;
              background-color: #fffbeb;
          }
          
          .amount-input {
              text-align: right;
          }
          
          /* Paginación */
          .pagination {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-top: 1rem;
              padding-top: 1rem;
              border-top: 1px solid #e5e7eb;
          }
          
          .pagination-info {
              color: #6b7280;
              font-size: 0.875rem;
          }
          
          .search-results {
              color: #059669;
              font-weight: 500;
          }
          
          .pagination .btn-outline {
              background-color: white;
              color: #374151;
              border: 1px solid #d1d5db;
          }
          
          .pagination .btn-outline:hover {
              background-color: #f9fafb;
              color: #1f2937;
              border-color: #9ca3af;
          }
          
          .pagination .btn-outline:disabled {
              background-color: #f9fafb;
              color: #9ca3af;
              border-color: #e5e7eb;
              cursor: not-allowed;
          }
          
          /* Estados vacíos */
          .empty-state {
              text-align: center;
              padding: 3rem 1rem;
              color: #6b7280;
          }
          
          .empty-content {
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              gap: 1rem;
          }
          
          .empty-icon {
              font-size: 3rem;
              opacity: 0.5;
          }
          
          .empty-content h3 {
              margin: 0;
              color: #374151;
              font-size: 1.25rem;
              font-weight: 600;
          }
          
          .empty-content p {
              margin: 0;
              font-size: 0.875rem;
          }
          
          .empty-hint {
              font-size: 0.8rem;
              opacity: 0.7;
          }
          
          /* Spinners de carga */
          .loading-container {
              display: flex;
              flex-direction: column;
              align-items: center;
              justify-content: center;
              padding: 2rem;
              gap: 1rem;
          }
          
          .loading-spinner, .loading-spinner-small {
              border: 3px solid #f3f4f6;
              border-top: 3px solid #6366f1;
              border-radius: 50%;
              animation: spin 1s linear infinite;
          }
          
          .loading-spinner {
              width: 2rem;
              height: 2rem;
          }
          
          .loading-spinner-small {
              width: 1.5rem;
              height: 1.5rem;
          }
          
          /* Mensajes */
          .message {
              padding: 0.75rem 1rem;
              border-radius: 6px;
              margin: 1rem 0;
              font-weight: 500;
          }
          
          .message-success {
              background-color: #d1fae5;
              color: #065f46;
              border: 1px solid #a7f3d0;
          }
          
          .message-error {
              background-color: #fee2e2;
              color: #991b1b;
              border: 1px solid #fca5a5;
          }
          
          /* Controles de tabla */
          .table-summary {
              padding: 0.5rem 0;
              margin-bottom: 1rem;
              font-size: 0.9rem;
              color: #6b7280;
          }
          
          .total-count {
              font-weight: 500;
          }
          
          /* Controles de edición centralizados */
          .edit-controls-centered {
              display: flex;
              justify-content: center;
              gap: 1rem;
              margin: 1rem 0;
              padding: 1rem;
              background: #f9fafb;
              border-radius: 8px;
              border: 1px solid #e5e7eb;
          }
          
          .edit-controls-centered .cancel-btn {
              background: #fff;
              color: #6b7280;
              border: 1px solid #d1d5db;
          }
          
          .edit-controls-centered .cancel-btn:hover {
              background: #f3f4f6;
              border-color: #9ca3af;
              color: #374151;
          }
          
          .edit-controls-centered .update-btn {
              background: #6366f1;
              color: white;
              border: 1px solid #6366f1;
          }
          
          .edit-controls-centered .update-btn:hover {
              background: #4f46e5;
              border-color: #4f46e5;
          }
          
          /* Animaciones */
          @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
          }
          
          .animate-fade-in {
              animation: fadeIn 0.2s ease-in-out;
          }
          
          @keyframes fadeIn {
              0% {
                  opacity: 0;
                  transform: translateY(-10px);
              }
              100% {
                  opacity: 1;
                  transform: translateY(0);
              }
          }
          
          /* Estilos de Modales */
          .modal-overlay {
              position: fixed;
              top: 0;
              left: 0;
              right: 0;
              bottom: 0;
              background-color: rgba(0, 0, 0, 0.6);
              display: flex;
              align-items: center;
              justify-content: center;
              z-index: 1000;
              padding: 1rem;
          }
          
          .modal-content {
              background: white;
              padding: 2rem;
              border-radius: 8px;
              box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
              width: 90%;
              max-width: 500px;
              max-height: 90vh;
              overflow-y: auto;
              animation: fadeInModal 0.3s ease-out;
          }
          
          .excel-modal {
              max-width: 900px;
              max-height: 90vh;
              overflow-y: auto;
          }
          
          .modal-header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin: -2rem -2rem 1.5rem -2rem;
              padding: 1.5rem 2rem;
              background: linear-gradient(135deg, #6366f1 0%, #4f46e5 100%);
              border-radius: 8px 8px 0 0;
          }
          
          .modal-title {
              color: white;
              margin: 0;
              font-size: 1.5rem;
              font-weight: 600;
          }
          
          .modal-close {
              background: none;
              border: none;
              font-size: 1.5rem;
              cursor: pointer;
              color: white;
              padding: 0;
              width: 30px;
              height: 30px;
              display: flex;
              align-items: center;
              justify-content: center;
              border-radius: 50%;
              transition: all 0.2s ease;
          }
          
          .modal-close:hover {
              background: rgba(255, 255, 255, 0.2);
          }
          
          .modal-body {
              padding: 0;
          }
          
          .modal-footer {
              display: flex;
              justify-content: flex-end;
              gap: 0.75rem;
              padding: 1rem 0 0 0;
              border-top: 1px solid #e5e7eb;
              margin-top: 1.5rem;
          }
          
          @keyframes fadeInModal {
              from { 
                  opacity: 0; 
                  transform: scale(0.95); 
              }
              to { 
                  opacity: 1; 
                  transform: scale(1); 
              }
          }
          
          /* Estilos para formularios en modales */
          .form-group {
              margin-bottom: 1.5rem;
          }
          
          .form-label {
              display: block;
              margin-bottom: 0.5rem;
              color: #374151;
              font-weight: 500;
              font-size: 0.875rem;
          }
          
          .form-input {
              width: 100%;
              padding: 0.75rem;
              border: 1px solid #d1d5db;
              border-radius: 6px;
              font-size: 0.875rem;
              transition: border-color 0.2s ease, box-shadow 0.2s ease;
          }
          
          .form-input:focus {
              outline: none;
              border-color: #6366f1;
              box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
          }
          
          .form-select {
              width: 100%;
              padding: 0.75rem;
              border: 1px solid #d1d5db;
              border-radius: 6px;
              font-size: 0.875rem;
              background-color: white;
              cursor: pointer;
              transition: border-color 0.2s ease;
          }
          
          .form-select:focus {
              outline: none;
              border-color: #6366f1;
              box-shadow: 0 0 0 3px rgba(99, 102, 241, 0.1);
          }
          
          .form-actions {
              margin-top: 1.5rem;
              display: flex;
              justify-content: flex-end;
              gap: 0.75rem;
          }
          
          /* Indicador de pasos */
          .step-indicator {
              display: flex;
              justify-content: center;
              align-items: center;
              margin: 2rem 0;
              padding: 0;
          }
          
          .step-item {
              display: flex;
              flex-direction: column;
              align-items: center;
              position: relative;
              flex: 1;
              max-width: 150px;
          }
          
          .step-item:not(:last-child)::after {
              content: '';
              position: absolute;
              top: 20px;
              left: 60%;
              width: calc(100% - 40px);
              height: 2px;
              background: #e5e7eb;
              z-index: 0;
          }
          
          .step-item.active:not(:last-child)::after {
              background: #6366f1;
          }
          
          .step-icon {
              width: 40px;
              height: 40px;
              border-radius: 50%;
              background: #f3f4f6;
              color: #6b7280;
              display: flex;
              align-items: center;
              justify-content: center;
              font-size: 1.2rem;
              margin-bottom: 0.5rem;
              position: relative;
              z-index: 1;
              transition: all 0.2s ease;
          }
          
          .step-item.active .step-icon {
              background: #6366f1;
              color: white;
          }
          
          .step-item.current .step-icon {
              background: #4f46e5;
              color: white;
              box-shadow: 0 0 0 4px rgba(99, 102, 241, 0.2);
          }
          
          .step-label {
              font-size: 0.75rem;
              color: #6b7280;
              text-align: center;
              font-weight: 500;
          }
          
          .step-item.active .step-label {
              color: #6366f1;
              font-weight: 600;
          }
          
          /* Estilos para secciones del modal */
          .upload-section {
              text-align: center;
              padding: 2rem 0;
          }
          
          .upload-area {
              border: 2px dashed #d1d5db;
              border-radius: 8px;
              padding: 3rem 2rem;
              cursor: pointer;
              transition: all 0.2s ease;
              position: relative;
          }
          
          .upload-area:hover, .upload-area.drag-active {
              border-color: #6366f1;
              background-color: #f8faff;
          }
          
          .upload-content {
              display: flex;
              flex-direction: column;
              align-items: center;
              gap: 1rem;
          }
          
          .upload-icon {
              font-size: 3rem;
              color: #6366f1;
          }
          
          .upload-text {
              color: #374151;
              font-size: 1rem;
              margin: 0;
          }
          
          .upload-link {
              color: #6366f1;
              text-decoration: underline;
              cursor: pointer;
          }
          
          .upload-hint {
              color: #6b7280;
              font-size: 0.875rem;
              margin: 0;
          }
          
          .upload-input {
              position: absolute;
              inset: 0;
              width: 100%;
              height: 100%;
              opacity: 0;
              cursor: pointer;
          }
          
          /* Estilos para validación */
          .validation-sections {
              margin: 1.5rem 0;
          }
          
          .validation-section {
              margin-bottom: 2rem;
              border: 1px solid #e5e7eb;
              border-radius: 8px;
              overflow: hidden;
          }
          
          .validation-section h4 {
              margin: 0;
              padding: 1rem;
              background-color: #f9fafb;
              border-bottom: 1px solid #e5e7eb;
              color: #374151;
              font-size: 1rem;
              font-weight: 600;
          }
          
          .validation-header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              padding: 1rem;
              background-color: #f9fafb;
              border-bottom: 1px solid #e5e7eb;
          }
          
          .validation-header h4 {
              margin: 0;
              padding: 0;
              background: none;
              border: none;
          }
          
          .validation-summary-inline {
              display: flex;
              gap: 1rem;
              font-size: 0.875rem;
          }
          
          .update-count, .create-count {
              padding: 0.25rem 0.75rem;
              border-radius: 12px;
              font-weight: 500;
              font-size: 0.75rem;
          }
          
          .update-count {
              background-color: #dbeafe;
              color: #1d4ed8;
          }
          
          .create-count {
              background-color: #d1fae5;
              color: #059669;
          }
          
          .record-type {
              padding: 0.25rem 0.5rem;
              border-radius: 4px;
              font-size: 0.75rem;
              font-weight: 500;
              white-space: nowrap;
          }
          
          .new-type {
              background-color: #d1fae5;
              color: #059669;
          }
          
          .update-type {
              background-color: #dbeafe;
              color: #1d4ed8;
          }
          
          .no-changes {
              color: #9ca3af;
              font-style: italic;
          }
          
          .validation-pagination {
              display: flex;
              justify-content: space-between;
              align-items: center;
              padding: 1rem;
              background-color: #f9fafb;
              border-top: 1px solid #e5e7eb;
              margin-top: auto;
          }
          
          .validation-pagination .pagination-info {
              color: #6b7280;
              font-size: 0.875rem;
              font-weight: 500;
          }
          
          .validation-pagination .btn-outline {
              background-color: white;
              color: #374151;
              border: 1px solid #d1d5db;
          }
          
          .validation-pagination .btn-outline:hover {
              background-color: #f9fafb;
              color: #1f2937;
              border-color: #9ca3af;
          }
          
          .validation-pagination .btn-outline:disabled {
              background-color: #f9fafb;
              color: #9ca3af;
              border-color: #e5e7eb;
              cursor: not-allowed;
          }
          
          /* Estilos para grupos expandibles */
          .group-header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              padding: 1rem;
              background-color: #f9fafb;
              border-bottom: 1px solid #e5e7eb;
              cursor: pointer;
              transition: all 0.2s ease;
              user-select: none;
          }
          
          .group-header:hover {
              background-color: #f3f4f6;
          }
          
          .group-header-left {
              display: flex;
              align-items: center;
              gap: 0.75rem;
          }
          
          .group-expand-icon {
              color: #6b7280;
              font-size: 0.875rem;
              transition: transform 0.2s ease;
              width: 12px;
              display: inline-block;
          }
          
          .group-expand-icon.expanded {
              transform: rotate(90deg);
          }
          
          .group-icon {
              font-size: 1.125rem;
          }
          
          .group-title {
              font-weight: 600;
              color: #374151;
              font-size: 1rem;
          }
          
          .group-description {
              color: #6b7280;
              font-size: 0.875rem;
              font-style: italic;
          }
          
          .group-pagination {
              display: flex;
              justify-content: space-between;
              align-items: center;
              padding: 0.75rem 1rem;
              background-color: #f9fafb;
              border-top: 1px solid #e5e7eb;
              margin-top: auto;
          }
          
          .group-pagination .btn-outline {
              background-color: white;
              color: #374151;
              border: 1px solid #d1d5db;
          }
          
          .group-pagination .btn-outline:hover {
              background-color: #f9fafb;
              color: #1f2937;
              border-color: #9ca3af;
          }
          
          .group-pagination .btn-outline:disabled {
              background-color: #f9fafb;
              color: #9ca3af;
              border-color: #e5e7eb;
              cursor: not-allowed;
          }
          
          .group-pagination .pagination-info {
              color: #6b7280;
              font-size: 0.8rem;
              font-weight: 500;
          }
          
          /* Estilos específicos para diferentes tipos de grupos */
          .error-section .group-header {
              background-color: #fef2f2;
              border-color: #fecaca;
          }
          
          .error-section .group-title {
              color: #dc2626;
          }
          
          .update-section .group-header {
              background-color: #eff6ff;
              border-color: #bfdbfe;
          }
          
          .update-section .group-title {
              color: #1d4ed8;
          }
          
          .create-section .group-header {
              background-color: #f0fdf4;
              border-color: #bbf7d0;
          }
          
          .create-section .group-title {
              color: #059669;
          }
          
          .delete-section .group-header {
              background-color: #fefbff;
              border-color: #e0e7ff;
          }
          
          .delete-section .group-title {
              color: #7c3aed;
          }
          
          .maintain-section .group-header {
              background-color: #f0f9ff;
              border-color: #bae6fd;
          }
          
          .maintain-section .group-title {
              color: #0284c7;
          }
          
          .maintain-record {
              background-color: #fefffe;
          }
          
          .maintain-record:hover {
              background-color: #f0f9ff;
          }
          
          .delete-record {
              background-color: #fef2f2;
          }
          
          .delete-record:hover {
              background-color: #fee2e2;
          }
          
          .record-type.not-found {
              background-color: #fef3c7;
              color: #92400e;
          }
          
          .record-type.user-marked {
              background-color: #fee2e2;
              color: #dc2626;
          }
          
          /* Mensajes de error */
          .error-messages {
              max-width: 300px;
          }
          
          .error-list {
              margin-bottom: 0.5rem;
          }
          
          .error-item {
              color: #dc2626;
              font-size: 0.75rem;
              margin-bottom: 0.25rem;
              padding: 0.25rem 0.5rem;
              background-color: #fee2e2;
              border-radius: 4px;
              border-left: 3px solid #dc2626;
          }
          
          .records-table {
              overflow-x: auto;
          }
          
          .records-table table {
              width: 100%;
              border-collapse: collapse;
              font-size: 0.875rem;
          }
          
          .records-table th {
              background-color: #f9fafb;
              padding: 0.75rem;
              text-align: left;
              font-weight: 600;
              color: #374151;
              border-bottom: 1px solid #e5e7eb;
          }
          
          .records-table td {
              padding: 0.75rem;
              border-bottom: 1px solid #f3f4f6;
              vertical-align: middle;
          }
          
          .records-table tr:hover {
              background-color: #f8fafc;
          }
          
          /* Estilos para mensajes */
          .message {
              padding: 1rem;
              border-radius: 6px;
              margin: 1rem 0;
              font-weight: 500;
              display: flex;
              align-items: flex-start;
              gap: 0.5rem;
          }
          
          .message-success {
              background-color: #d1fae5;
              color: #065f46;
              border: 1px solid #a7f3d0;
          }
          
          .message-error {
              background-color: #fee2e2;
              color: #991b1b;
              border: 1px solid #fca5a5;
          }
          
          /* Estilos para resumen de operación mejorado */
          .results-summary {
              background-color: #f8fafc;
              border: 1px solid #e2e8f0;
              border-radius: 8px;
              padding: 1.5rem;
              margin: 1.5rem 0;
              position: relative;
          }
          
          .summary-header {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 1rem;
          }
          
          .summary-title {
              margin: 0;
              color: #1f2937;
              font-size: 1.125rem;
              font-weight: 600;
              text-align: center;
              flex: 1;
          }
          
          .summary-close-btn {
              background: none;
              border: none;
              font-size: 1.5rem;
              cursor: pointer;
              color: #6b7280;
              padding: 0;
              width: 30px;
              height: 30px;
              display: flex;
              align-items: center;
              justify-content: center;
              border-radius: 50%;
              transition: all 0.2s ease;
          }
          
          .summary-close-btn:hover {
              background-color: #e5e7eb;
              color: #374151;
          }
          
          .summary-stats {
              display: grid;
              grid-template-columns: repeat(auto-fit, minmax(120px, 1fr));
              gap: 1rem;
          }
          
          .stat-card {
              background: white;
              border-radius: 8px;
              padding: 1rem;
              text-align: center;
              box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
              border-top: 4px solid;
          }
          
          .stat-card.success {
              border-top-color: #10b981;
          }
          
          .stat-card.info {
              border-top-color: #3b82f6;
          }
          
          .stat-card.warning {
              border-top-color: #f59e0b;
          }
          
          .stat-card.error {
              border-top-color: #ef4444;
          }
          
          .stat-number {
              font-size: 2rem;
              font-weight: 700;
              line-height: 1;
              margin-bottom: 0.25rem;
          }
          
          .stat-card.success .stat-number {
              color: #10b981;
          }
          
          .stat-card.info .stat-number {
              color: #3b82f6;
          }
          
          .stat-card.warning .stat-number {
              color: #f59e0b;
          }
          
          .stat-card.error .stat-number {
              color: #ef4444;
          }
          
          .stat-label {
              font-size: 0.875rem;
              color: #6b7280;
              font-weight: 500;
          }
          
          /* Estilos para contador de bonos arriba de la tabla */
          .table-summary {
              display: flex;
              justify-content: space-between;
              align-items: center;
              margin-bottom: 1rem;
              padding: 0.75rem 1rem;
              background-color: #f8fafc;
              border-radius: 6px;
              border: 1px solid #e2e8f0;
          }
          
          .total-count {
              font-size: 0.875rem;
              font-weight: 600;
              color: #374151;
          }
          
          /* Estilos para controles centralizados */
          .controls-right {
              display: flex;
              gap: 0.5rem;
              align-items: center;
          }
          
          .add-bonus-btn {
              background-color: #10b981;
              color: white;
              border-color: #10b981;
          }
          
          .add-bonus-btn:hover {
              background-color: #059669;
              border-color: #059669;
          }
          
          .edit-controls-centered {
              display: flex;
              justify-content: center;
              align-items: center;
              gap: 1rem;
              margin: 1rem 0;
              padding: 1rem;
              background-color: #fffbeb;
              border: 1px solid #fbbf24;
              border-radius: 6px;
          }
          
          .cancel-btn {
              color: #dc2626;
              border-color: #dc2626;
              background-color: white;
          }
          
          .cancel-btn:hover {
              color: white;
              background-color: #dc2626;
              border-color: #dc2626;
          }
          
          .update-btn {
              color: white;
              background-color: #059669;
              border-color: #059669;
          }
          
          .update-btn:hover {
              background-color: #047857;
              border-color: #047857;
          }
          
          /* Responsive */
          @media (max-width: 1024px) {
              .precios-layout {
                  grid-template-columns: 1fr;
                  gap: 1rem;
              }
              .catalog-list-column {
                  height: auto;
                  max-height: 40vh;
                  margin-bottom: 1.5rem;
                  min-width: auto;
              }
              .pricing-column {
                  min-height: auto;
              }
          }
          
          @media (max-width: 768px) {
              .header {
                  padding: 1rem;
              }
              .header-title {
                  font-size: 1.25rem;
              }
              .precios-layout {
                  padding: 1rem;
                  gap: 0.5rem;
              }
              .catalog-list-column {
                  max-height: 35vh;
              }
              .pricing-controls {
                  flex-direction: column;
                  align-items: stretch;
              }
              .search-box {
                  max-width: none;
              }
              
              .modal-content {
                  margin: 1rem;
                  padding: 1.5rem;
                  width: calc(100% - 2rem);
              }
              
              .excel-modal {
                  max-width: 95vw;
                  margin: 1rem;
              }
              
              .step-indicator {
                  flex-direction: column;
                  gap: 1rem;
              }
              
              .step-item:not(:last-child)::after {
                  display: none;
              }
          }
      `}</style>
    </div>
  );
}